import { useState, useEffect, useRef, useCallback } from "react";

// ---------------------------------------------
//  CONFIGURACIÓN <- pon tus claves aqui
// ---------------------------------------------
const CONFIG = {
 API_KEY:              "borradas",
  SECRET_KEY:           "borradas",
  // LOCAL: "http://localhost:3001"
  // RENDER (nube gratis): cambia por tu URL de Render cuando lo despliegues
  PROXY: process.env.REACT_APP_PROXY_URL || "http://localhost:3001",
  ORDER_SIZE_USDT:      10,
  CONFIDENCE_THRESHOLD: 75,

  // Comisiones Bitunix VIP 0 (ordenes de mercado = siempre taker)
  FEE_TAKER:            0.0010,  // 0.10% por operación
  FEE_MAKER:            0.0008,  // 0.08% órdenes límite (referencia)
  FEE_ROUNDTRIP:        0.0020,  // 0.20% coste total compra+venta (lo que usa el servidor)
  // El precio debe subir al menos 0.20% para no perder dinero
  FEE_BREAKEVEN_PCT:    0.20,    // % mínimo de subida para cubrir comisiones
};

const DEFAULT_COINS = [
  { id: "bitcoin",  symbol: "BTC",   name: "Bitcoin",        pair: "BTCUSDT",   quote: "USDT", decimals: 6 },
  { id: "ethereum", symbol: "ETH",   name: "Ethereum",       pair: "ETHUSDT",   quote: "USDT", decimals: 5 },
  { id: "solana",   symbol: "SOL",   name: "Solana",         pair: "SOLUSDT",   quote: "USDT", decimals: 3 },
  { id: "sui",      symbol: "SUI",   name: "Sui",            pair: "SUIUSDT",   quote: "USDT", decimals: 2 },
  { id: "aster",    symbol: "ASTER", name: "Astar",          pair: "ASTERUSDT", quote: "USDT", decimals: 1 },
  { id: "ton",      symbol: "TON",   name: "Toncoin",        pair: "TONUSDT",   quote: "USDT", decimals: 2 },
  { id: "hype",     symbol: "HYPE",  name: "Hyperliquid",    pair: "HYPEUSDT",  quote: "USDT", decimals: 3 },
  { id: "xrp",      symbol: "XRP",   name: "XRP",            pair: "XRPUSDT",   quote: "USDT", decimals: 2 },
  { id: "pengu",    symbol: "PENGU", name: "Pudgy Penguins", pair: "PENGUUSDT", quote: "USDT", decimals: 0 },
];

// ---------------------------------------------
//  INDICADORES TÉCNICOS
// ---------------------------------------------
function calcRSI(prices, period = 14) {
  if (prices.length < period + 1) return null;
  const changes = prices.slice(1).map((p, i) => p - prices[i]);
  let gains = 0, losses = 0;
  changes.slice(0, period).forEach(c => { if (c > 0) gains += c; else losses -= c; });
  let avgGain = gains / period, avgLoss = losses / period;
  for (let i = period; i < changes.length; i++) {
    const c = changes[i];
    avgGain = (avgGain * (period - 1) + (c > 0 ? c : 0)) / period;
    avgLoss = (avgLoss * (period - 1) + (c < 0 ? -c : 0)) / period;
  }
  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return Math.round(100 - 100 / (1 + rs));
}

function calcEMA(prices, period) {
  if (prices.length < period) return null;
  const k = 2 / (period + 1);
  let ema = prices.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < prices.length; i++) ema = prices[i] * k + ema * (1 - k);
  return ema;
}

function calcMACD(prices) {
  if (prices.length < 35) return null;
  // Calcular línea MACD para cada punto para poder hacer EMA(9) correctamente
  const macdSeries = [];
  for (let i = 25; i < prices.length; i++) {
    const slice = prices.slice(0, i + 1);
    const e12 = calcEMA(slice, 12);
    const e26 = calcEMA(slice, 26);
    if (e12 && e26) macdSeries.push(e12 - e26);
  }
  if (macdSeries.length < 9) return null;
  const macdLine  = macdSeries[macdSeries.length - 1];
  const signalLine = calcEMA(macdSeries, 9);
  if (!signalLine) return null;
  return { macd: macdLine, signal: signalLine, histogram: macdLine - signalLine };
}

function calcSMA(prices, period) {
  if (prices.length < period) return null;
  return prices.slice(-period).reduce((a, b) => a + b, 0) / period;
}

function getIndicators(prices) {
  if (!prices || prices.length < 5) return null;
  const rsi  = calcRSI(prices);
  const macd = calcMACD(prices);
  const sma7  = calcSMA(prices, Math.min(7, prices.length));
  const sma20 = calcSMA(prices, Math.min(20, prices.length));
  const last  = prices[prices.length - 1];
  const trend = sma7 && sma20 ? (sma7 > sma20 ? "ALCISTA" : "BAJISTA") : "NEUTRAL";
  return { rsi, macd, sma7, sma20, trend, last };
}

// ---------------------------------------------
//  FIRMA HMAC BITUNIX
// ---------------------------------------------
// ─── Autenticacion y API calls ──────────────────────────────────

function getToken() {
  try {
    // Usar master token si está configurado (no expira)
    const master = process.env.REACT_APP_MASTER_TOKEN;
    if (master) return master;
    return (()=>{try{return localStorage.getItem(TOKEN_KEY)}catch(_){return null}})() || "";
  } catch(_) { return (()=>{try{return localStorage.getItem(TOKEN_KEY)}catch(_){return null}})() || ""; }
}

async function apiGet(path, ms = 10000) {
  const ctrl = new AbortController();
  const to   = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(`${CONFIG.PROXY}${path}`, {
      headers: { "x-bot-token": getToken() },
      signal: ctrl.signal,
    });
    clearTimeout(to);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  } catch(e) {
    clearTimeout(to);
    console.error(`apiGet ${path}:`, e.message);
    return { error: e.message };
  }
}

async function apiPost(path, body = {}, ms = 12000) {
  const ctrl = new AbortController();
  const to   = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(`${CONFIG.PROXY}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-bot-token": getToken() },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    clearTimeout(to);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  } catch(e) {
    clearTimeout(to);
    console.error(`apiPost ${path}:`, e.message);
    return { error: e.message };
  }
}

// ---------------------------------------------
//  HISTORIAL PERSISTENTE (localStorage)
// ---------------------------------------------
const STORAGE_KEY    = "bitunix_bot_trades_v2";
const MEMORY_KEY     = "bitunix_bot_memory_v2";
const ANALYSES_KEY   = "bot_analyses";
const LOGS_KEY       = "bot_logs";
const TOKEN_KEY      = "bot_token";
const OHLC_CACHE_KEY = "ohlc_cache";

function loadTrades() {
  try { return JSON.parse((()=>{try{return localStorage.getItem(STORAGE_KEY)}catch(_){return null}})() || "[]"); } catch { return []; }
}
function saveTrades(trades) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(trades.slice(-200))); } catch(_) {}
}
function loadMemory() {
  try { return JSON.parse((()=>{try{return localStorage.getItem(MEMORY_KEY)}catch(_){return null}})() || "{}"); } catch { return {}; }
}
function saveMemory(memory) {
  try { localStorage.setItem(MEMORY_KEY, JSON.stringify(memory)); } catch(_) {}
}

// ---------------------------------------------
//  HELPERS
// ---------------------------------------------
const fmt$ = (n) => {
  if (n == null || isNaN(n)) return "---";
  if (n === 0) return "$0";
  if (n >= 1000)  return "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (n >= 1)     return "$" + Number(n).toFixed(2);
  if (n >= 0.01)  return "$" + Number(n).toFixed(4);
  return "$" + Number(n).toFixed(6);
};
const fmtPct = (n) => (n >= 0 ? "+" : "") + Number(n).toFixed(2) + "%";

// ---------------------------------------------
//  COMPONENTES UI
// ---------------------------------------------
function Sparkline({ data, color, width = 80, height = 28 }) {
  if (!data || data.length < 2) return null;
  const clean = data.filter(v => v != null && !isNaN(v) && isFinite(v));
  if (clean.length < 2) return null;
  const min = Math.min(...clean), max = Math.max(...clean), range = max - min || 1;
  const pts = clean.map((v, i) => `${(i / (clean.length - 1)) * width},${height - ((v - min) / range) * height}`).join(" ");
  return <svg width={width} height={height} style={{ display: "block" }}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}


function Signal({ action, confidence }) {
  const cfg = {
    BUY:  { color:"#00d4a0", bg:"rgba(0,212,160,0.12)", icon:"▲" },
    SELL: { color:"#ff4466", bg:"rgba(255,68,102,0.12)", icon:"▼" },
    HOLD: { color:"#f5a623", bg:"rgba(245,166,35,0.12)",  icon:"◆" },
  }[action||"HOLD"] || { color:"#8892a4", bg:"rgba(255,255,255,0.05)", icon:"—" };
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:8,
      background:cfg.bg, border:`1.5px solid ${cfg.color}`,
      borderRadius:8, padding:"6px 14px" }}>
      <span style={{ fontSize:18, color:cfg.color }}>{cfg.icon}</span>
      <span style={{ fontSize:14, fontWeight:700, color:cfg.color }}>{action||"—"}</span>
      <span style={{ fontSize:12, color:"#6b7a8d" }}>Confianza:</span>
      <span style={{ fontSize:13, fontFamily:"monospace", color:cfg.color }}>{confidence||0}%</span>
    </div>
  );
}

function TypedText({ text }) {
  const [shown, setShown] = useState("");
  const [i, setI] = useState(0);
  useEffect(() => { setShown(""); setI(0); }, [text]);
  useEffect(() => {
    if (!text || i >= text.length) return;
    const t = setTimeout(() => { setShown(text.slice(0, i + 1)); setI(x => x + 1); }, 8);
    return () => clearTimeout(t);
  }, [text, i]);
  return <span>{shown}{i < (text?.length || 0) && <span style={{ opacity: .5 }}>▌</span>}</span>;
}

function Badge({ ok, label }) {
  return <span style={{ fontSize: 9, letterSpacing: 1, padding: "2px 8px", borderRadius: 20,
    background: ok ? "rgba(0,212,160,0.1)" : "rgba(255,68,102,0.1)",
    color: ok ? "#00d4a0" : "#ff4466",
    border: `1px solid ${ok ? "rgba(0,212,160,0.25)" : "rgba(255,68,102,0.25)"}` }}>{label}</span>;
}

// ---------------------------------------------
//  APP PRINCIPAL
// ---------------------------------------------
// ─── Pantalla de Login ───────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const handleLogin = async () => {
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiPost("/login", { password }, 8000);
      if (data.token) {
        try { localStorage.setItem(TOKEN_KEY, data.token); } catch(_) {}
        onLogin(data.token);
      } else {
        setError(data.error || data.error || "Contraseña incorrecta");
      }
    } catch (e) {
      setError("No se puede conectar al servidor. Verifica que Render está activo.");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#080c10", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Space Mono', monospace" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap');`}</style>
      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(0,255,170,0.2)", borderRadius: 16, padding: 40, width: 320, textAlign: "center" }}>
        <div style={{ fontSize: 10, letterSpacing: 4, color: "#00d4a0", marginBottom: 8 }}>ACCESO SEGURO</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: "#fff", marginBottom: 32 }}>BITUNIX BOT</div>
        <input
          type="password"
          placeholder="Contraseña"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          style={{ width: "100%", padding: "12px 16px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontFamily: "'Space Mono',monospace", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" }}
        />
        {error && <div style={{ color: "#ff4466", fontSize: 11, marginBottom: 12 }}>{error}</div>}
        <button onClick={handleLogin} disabled={loading} style={{ width: "100%", padding: "12px", background: "rgba(0,212,160,0.15)", border: "1px solid #00d4a0", borderRadius: 8, color: "#00d4a0", fontFamily: "'Space Mono',monospace", fontSize: 12, cursor: loading ? "not-allowed" : "pointer", letterSpacing: 1 }}>
          {loading ? "CONECTANDO..." : "ENTRAR"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  TRADING CHART — Lightweight Charts v4
// ─────────────────────────────────────────────
function TradingChart({ coin, stopLosses, trades, prices }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef(null);
  const volRef       = useRef(null);
  const slLineRef    = useRef(null);
  const tpLineRef    = useRef(null);
  const entryLineRef = useRef(null);
  const [tf, setTf]        = useState("1h");
  const [loading, setLoading] = useState(true);
  const [error, setError]    = useState(null);
  const [info, setInfo]      = useState(null); // {o,h,l,c,v,t}

  const TF_OPTIONS = [
    { label:"15m", value:"15m" },
    { label:"1H",  value:"1h" },
    { label:"4H",  value:"4h" },
    { label:"1D",  value:"1d" },
  ];

  // Cargar o actualizar librería y chart
  useEffect(() => {
    // Cargar LightweightCharts si no está disponible
    const loadChart = () => {
      if (window.LightweightCharts) { initChart(); return; }
      const script = document.createElement("script");
      script.src = "https://unpkg.com/lightweight-charts@4.1.3/dist/lightweight-charts.standalone.production.js";
      script.onload = initChart;
      script.onerror = () => setError("Error cargando librería de gráfico");
      document.head.appendChild(script);
    };

    const initChart = () => {
      if (!containerRef.current || !window.LightweightCharts) return;
      // Destruir chart anterior si existe
      if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }

      const chart = window.LightweightCharts.createChart(containerRef.current, {
        layout: {
          background:   { type: "solid", color: "#0a0e14" },
          textColor:    "#6b7a8d",
          fontSize:     11,
          fontFamily:   "'Space Mono', monospace",
        },
        grid: {
          vertLines:   { color: "rgba(255,255,255,0.04)" },
          horzLines:   { color: "rgba(255,255,255,0.04)" },
        },
        crosshair: {
          mode:        1,
          vertLine:    { color: "rgba(255,255,255,0.15)", labelBackgroundColor: "#1a2030" },
          horzLine:    { color: "rgba(255,255,255,0.15)", labelBackgroundColor: "#1a2030" },
        },
        rightPriceScale: {
          borderColor:  "rgba(255,255,255,0.06)",
          textColor:    "#6b7a8d",
          scaleMargins: { top: 0.08, bottom: 0.22 },
        },
        timeScale: {
          borderColor:  "rgba(255,255,255,0.06)",
          timeVisible:  true,
          secondsVisible: false,
          rightBarStaysOnScroll: true,
          fixLeftEdge:  true,
          fixRightEdge: true,
        },
        handleScroll:    true,
        handleScale:     true,
      });

      // Serie de velas
      const candleSeries = chart.addCandlestickSeries({
        upColor:          "#00d4a0",
        downColor:        "#ff4466",
        borderUpColor:    "#00d4a0",
        borderDownColor:  "#ff4466",
        wickUpColor:      "#00d4a0",
        wickDownColor:    "#ff4466",
        priceScaleId:     "right",
      });

      // Serie de volumen
      const volSeries = chart.addHistogramSeries({
        color:            "rgba(77,166,255,0.15)",
        priceFormat:      { type: "volume" },
        priceScaleId:     "vol",
        scaleMargins:     { top: 0.80, bottom: 0.0 },
      });
      chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.80, bottom: 0.0 } });

      // Evento crosshair para info OHLCV
      chart.subscribeCrosshairMove(param => {
        if (!param.time || !param.seriesData) { setInfo(null); return; }
        const candle = param.seriesData.get(candleSeries);
        const vol    = param.seriesData.get(volSeries);
        if (candle) setInfo({ ...candle, v: vol?.value });
      });

      chartRef.current   = chart;
      seriesRef.current  = candleSeries;
      volRef.current     = volSeries;
      loadData(candleSeries, volSeries, chart);

      // Responsive resize
      const ro = new ResizeObserver(() => {
        if (containerRef.current && chartRef.current) {
          chartRef.current.applyOptions({
            width:  containerRef.current.clientWidth,
            height: containerRef.current.clientHeight,
          });
        }
      });
      ro.observe(containerRef.current);
      return () => { ro.disconnect(); };
    };

    loadChart();
    return () => {
      if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }
    };
  }, [coin.id, tf]); // eslint-disable-line

  // Cargar datos OHLC
  const loadData = async (candleSeries, volSeries, chart) => {
    if (!coin) return;
    setLoading(true); setError(null);
    try {
      const limit = tf === "1d" ? 90 : tf === "4h" ? 120 : tf === "1h" ? 100 : 200;
      const data  = await apiGet(`/api/klines?symbol=${coin.pair}&interval=${tf}&limit=${limit}`);
      if (!data?.ohlc?.length) throw new Error("Sin datos OHLC");

      const candles = data.ohlc
        .filter(c => c.t > 0 && c.c > 0)
        .map(c => ({
          time:  Math.floor(c.t / 1000),
          // Spot devuelve open/close en orden estándar — no invertir
          open:  c.o, high: c.h, low: c.l, close: c.c,
        }))
        .sort((a,b) => a.time - b.time);

      const vols = data.ohlc
        .filter(c => c.t > 0 && c.c > 0)
        .map(c => ({
          time:  Math.floor(c.t / 1000),
          value: c.v,
          color: c.c >= c.o ? "rgba(0,212,160,0.2)" : "rgba(255,68,102,0.2)",
        }))
        .sort((a,b) => a.time - b.time);

      if (candleSeries && candles.length > 0) {
        candleSeries.setData(candles);
        volSeries.setData(vols);

        // Añadir marcadores de trades
        addTradeMarkers(candleSeries);
        // Añadir líneas SL/TP/Entry
        updatePositionLines(chart, candleSeries);

        chart.timeScale().fitContent();
      }
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
  };

  // Marcadores de entrada/salida en el gráfico
  const addTradeMarkers = (series) => {
    if (!series || !trades?.length) return;
    const markers = [];
    const coinTrades = trades.filter(t => t.symbol === coin.symbol && t.price > 0 && t.ts);
    // Rango de velas visible — solo mostrar trades dentro del rango
    const _tfSec = tf === "1d" ? 86400 : tf === "4h" ? 14400 : tf === "1h" ? 3600 : 900;
    const _rangeStart = Math.floor(Date.now()/1000) - (100 * _tfSec);
    coinTrades.forEach(t => {
      const ts = Math.floor(t.ts / 1000);
      if (ts < _rangeStart) return; // fuera del rango visible
      // Alinear al inicio del intervalo de la vela
      const alignedTs = Math.floor(ts / _tfSec) * _tfSec;
      if (t.action === "BUY") {
        markers.push({ time:alignedTs, position:"belowBar", color:"#00d4a0", shape:"arrowUp", text:`▲ $${parseFloat(t.price).toFixed(4)}`, size:2 });
      } else if (t.action === "SELL") {
        const pnl = parseFloat(t.pnl||0);
        markers.push({ time:alignedTs, position:"aboveBar", color:pnl>=0?"#00d4a0":"#ff4466", shape:"arrowDown", text:`▼ ${pnl>=0?"+":""}${pnl.toFixed(2)}%`, size:2 });
      }
    });
    if (markers.length > 0) {
      try {
        markers.sort((a,b) => a.time - b.time);
        // Deduplicar timestamps iguales
        const unique = markers.filter((m,i) => i===0 || m.time !== markers[i-1].time);
        series.setMarkers(unique);
      } catch(_) {}
    }
  };

  // Líneas horizontales SL / TP / Entrada
  const updatePositionLines = (chart, series) => {
    if (!chart || !series) return;
    const pos = stopLosses?.[coin.id];
    if (!pos) {
      // Limpiar líneas si no hay posición
      [slLineRef, tpLineRef, entryLineRef].forEach(ref => {
        if (ref.current) { try { series.removePriceLine(ref.current); } catch(_) {} ref.current = null; }
      });
      return;
    }

    // Limpiar líneas anteriores
    [slLineRef, tpLineRef, entryLineRef].forEach(ref => {
      if (ref.current) { try { series.removePriceLine(ref.current); } catch(_) {} ref.current = null; }
    });

    if (pos.entryPrice > 0) {
      entryLineRef.current = series.createPriceLine({
        price:       pos.entryPrice,
        color:       "#f5a623",
        lineWidth:   2,
        lineStyle:   0,
        axisLabelVisible: true,
        title:       "E",
      });
    }
    if (pos.sl > 0) {
      slLineRef.current = series.createPriceLine({
        price:       pos.sl,
        color:       "#ff4466",
        lineWidth:   1,
        lineStyle:   2,
        axisLabelVisible: true,
        title:       "SL",
      });
    }
    if (pos.tp > 0) {
      tpLineRef.current = series.createPriceLine({
        price:       pos.tp,
        color:       "#00d4a0",
        lineWidth:   1,
        lineStyle:   2,
        axisLabelVisible: true,
        title:       "TP",
      });
    }
  };

  // Actualizar precio en tiempo real — actualiza la vela actual sin crear barra nueva
  useEffect(() => {
    if (!seriesRef.current || !prices?.[coin.id]) return;
    const p = prices[coin.id];
    if (p <= 0) return;
    try {
      // Calcular timestamp del inicio del intervalo actual
      const tfSeconds = tf === "1d" ? 86400 : tf === "4h" ? 14400 : tf === "1h" ? 3600 : 900;
      const now = Math.floor(Date.now() / 1000);
      const candleTime = Math.floor(now / tfSeconds) * tfSeconds;
      seriesRef.current.update({
        time:  candleTime,
        open:  p, high: p, low: p, close: p,
      });
    } catch(_) {}
  }, [prices?.[coin.id]]); // eslint-disable-line

  // Actualizar líneas de posición cuando cambia
  useEffect(() => {
    if (!chartRef.current || !seriesRef.current) return;
    updatePositionLines(chartRef.current, seriesRef.current);
    addTradeMarkers(seriesRef.current);
  }, [stopLosses?.[coin.id], trades?.length]); // eslint-disable-line

  const pos     = stopLosses?.[coin.id];
  const curP    = prices?.[coin.id] || 0;
  const pnl     = pos?.entryPrice > 0 && curP > 0 ? ((curP - pos.entryPrice) / pos.entryPrice * 100) : null;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"#0a0e14" }}>

      {/* Header del gráfico */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"8px 12px", borderBottom:"1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>

        {/* Info de la moneda */}
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div>
            <span style={{ fontSize:13, fontWeight:700, color:"#e8eaf0", fontFamily:"monospace" }}>
              {coin.symbol}
            </span>
            <span style={{ fontSize:9, color:"#4a5568", marginLeft:6 }}>{coin.pair} · {tf.toUpperCase()}</span>
          </div>
          {curP > 0 && (
            <span style={{ fontSize:12, fontWeight:700, fontFamily:"monospace",
              color: pnl !== null ? (pnl >= 0 ? "#00d4a0" : "#ff4466") : "#e8eaf0" }}>
              ${curP.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:4 })}
            </span>
          )}
          {pnl !== null && (
            <span style={{ fontSize:10, fontFamily:"monospace",
              color: pnl >= 0 ? "#00d4a0" : "#ff4466",
              background: pnl >= 0 ? "rgba(0,212,160,0.1)" : "rgba(255,68,102,0.1)",
              padding:"2px 6px", borderRadius:4 }}>
              {pnl >= 0 ? "+" : ""}{pnl.toFixed(2)}%
            </span>
          )}
        </div>

        {/* Info OHLCV del crosshair */}
        {info && (
          <div style={{ display:"flex", gap:8, fontSize:9, fontFamily:"monospace" }}>
            {[
              { l:"O", v:info.open,  c:"#e8eaf0" },
              { l:"H", v:info.high,  c:"#00d4a0" },
              { l:"L", v:info.low,   c:"#ff4466" },
              { l:"C", v:info.close, c:"#e8eaf0" },
            ].map(({ l, v, c }) => (
              <span key={l} style={{ color:"#4a5568" }}>
                {l}: <span style={{ color:c }}>{parseFloat(v).toFixed(4)}</span>
              </span>
            ))}
          </div>
        )}

        {/* Selectores de timeframe */}
        <div style={{ display:"flex", gap:3 }}>
          {TF_OPTIONS.map(t => (
            <button key={t.value} onClick={() => setTf(t.value)}
              style={{ padding:"3px 8px", borderRadius:4, cursor:"pointer",
                fontFamily:"monospace", fontSize:9,
                background: tf===t.value ? "rgba(0,212,160,0.15)" : "rgba(255,255,255,0.03)",
                border:     tf===t.value ? "1px solid #00d4a0"    : "1px solid rgba(255,255,255,0.07)",
                color:      tf===t.value ? "#00d4a0"              : "#6b7a8d",
              }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Posición activa (si hay) */}
      {pos && (() => {
        const tpHits   = pos.tpHits || 0;
        const isBreakeven = pos.entryPrice > 0 && pos.sl >= pos.entryPrice;
        const slColor  = isBreakeven ? "#00d4a0" : tpHits > 0 ? "#f5a623" : "#ff4466";
        const soldPct  = tpHits === 1 ? (pos.adxAtEntry > 35 ? 30 : pos.adxAtEntry > 20 ? 40 : 50)
                       : tpHits === 2 ? (pos.adxAtEntry > 35 ? 60 : 75) : 0;
        return (
          <div style={{ padding:"5px 12px", background:"rgba(0,212,160,0.04)",
            borderBottom:"1px solid rgba(0,212,160,0.1)", flexShrink:0 }}>
            {/* Fila de datos */}
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom: pos.tp2 ? 5 : 0 }}>
              {[
                { l:"ENTRADA", v:`$${pos.entryPrice?.toFixed(4)||"—"}`, c:"#f5a623" },
                { l:"SL",      v:`$${pos.sl?.toFixed(4)||"—"}`,         c:slColor },
                { l:"TP",      v:`$${pos.tp?.toFixed(4)||"—"}`,         c:"#00d4a0" },
                { l:"QTY",     v:`${pos.qty} ${coin.symbol}`,            c:"#8892a4" },
                { l:"PnL",     v:pnl !== null ? `${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}%` : "—",
                  c: pnl !== null ? (pnl >= 0 ? "#00d4a0" : "#ff4466") : "#8892a4" },
              ].map(({ l, v, c }) => (
                <div key={l} style={{ fontSize:8, fontFamily:"monospace" }}>
                  <span style={{ color:"#4a5568" }}>{l}: </span>
                  <span style={{ color:c, fontWeight:700 }}>{v}</span>
                </div>
              ))}
              {isBreakeven && <div style={{ fontSize:7, color:"#00d4a0", fontFamily:"monospace", alignSelf:"center" }}>🔒 BREAKEVEN</div>}
            </div>
            {/* Barra progreso TP1→TP2→TP3 */}
            {pos.tp2 && pos.tp3 && (
              <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                {["TP1","TP2","TP3"].map((lbl, i) => {
                  const done = tpHits > i;
                  const active = tpHits === i;
                  return (
                    <div key={lbl} style={{ display:"flex", alignItems:"center", gap:3 }}>
                      <div style={{
                        padding:"1px 6px", borderRadius:3, fontSize:7, fontFamily:"monospace",
                        fontWeight:700,
                        background: done ? "rgba(0,212,160,0.25)" : active ? "rgba(245,166,35,0.2)" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${done ? "#00d4a0" : active ? "#f5a623" : "rgba(255,255,255,0.08)"}`,
                        color: done ? "#00d4a0" : active ? "#f5a623" : "#4a5568",
                      }}>
                        {done ? `${lbl} ✓` : lbl}
                      </div>
                      {i < 2 && <div style={{ width:12, height:1, background:"rgba(255,255,255,0.1)" }}/>}
                    </div>
                  );
                })}
                {soldPct > 0 && (
                  <div style={{ fontSize:7, color:"#8892a4", fontFamily:"monospace", marginLeft:4 }}>
                    {soldPct}% vendido
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Contenedor del gráfico */}
      <div style={{ flex:1, position:"relative", minHeight:0 }}>
        {loading && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center",
            justifyContent:"center", background:"#0a0e14", zIndex:10 }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ width:24, height:24, border:"2px solid rgba(0,212,160,0.2)",
                borderTopColor:"#00d4a0", borderRadius:"50%",
                animation:"spin 0.8s linear infinite", margin:"0 auto 8px" }}/>
              <div style={{ fontSize:9, color:"#4a5568", fontFamily:"monospace" }}>
                Cargando {coin.symbol} {tf.toUpperCase()}...
              </div>
            </div>
          </div>
        )}
        {error && (
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center",
            justifyContent:"center", background:"#0a0e14", zIndex:10 }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:20, marginBottom:6 }}>⚠️</div>
              <div style={{ fontSize:10, color:"#ff4466", fontFamily:"monospace" }}>{error}</div>
              <button onClick={() => loadData(seriesRef.current, volRef.current, chartRef.current)}
                style={{ marginTop:8, padding:"4px 12px", background:"rgba(0,212,160,0.1)",
                  border:"1px solid rgba(0,212,160,0.3)", color:"#00d4a0",
                  borderRadius:4, cursor:"pointer", fontSize:9, fontFamily:"monospace" }}>
                Reintentar
              </button>
            </div>
          </div>
        )}
        <div ref={containerRef} style={{ width:"100%", height:"100%" }} />
      </div>
    </div>
  );
}


function BotDashboard({ token, onLogout }) {
  // ── UI state ──
  const [activeTab,    setActiveTab]    = useState("chart");
  const [fearGreed,    setFearGreed]    = useState(null);
  const [globalRegime, setGlobalRegime] = useState(null);
  const [trailingStop,   setTrailingStop]   = useState(true);
  const [sentimentFilter,setSentimentFilter]= useState(true);
  const [dynamicRisk,    setDynamicRisk]    = useState(true);
  const [volumeFilter,   setVolumeFilter]   = useState(true);
  const [fundingRates, setFundingRates] = useState({});
  const [botStats,     setBotStats]     = useState(null);
  const [equityData,   setEquityData]   = useState([]);
  const [paperStats,   setPaperStats]   = useState(null);

  // ── Coin list (must be first — other state depends on it) ──
  const [coinList,     setCoinList]     = useState(DEFAULT_COINS);
  const COINS = coinList;

  // ── Market state ──
  const [prices,       setPrices]       = useState({});
  const [changes,      setChanges]      = useState({});
  const [indicators,   setIndicators]   = useState({});
  const [coin,         setCoin]         = useState(COINS[0]);

  // ── Bot state ──
  const [analyzing,    setAnalyzing]    = useState(false);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [allProgress,  setAllProgress]  = useState("");
  const [autoMode,     setAutoMode]     = useState(false);
  const [paperMode,    setPaperMode]    = useState(true);
  const [analysis,     setAnalysis]     = useState(null);
  const [allAnalyses,  setAllAnalyses]  = useState({});  // siempre del servidor
  const [proxyOk,      setProxyOk]      = useState(false);
  const [logs,         setLogs]         = useState([]);   // siempre del servidor
  const [trades,       setTrades]       = useState([]);
  const [memory,       setMemory]       = useState({});
  const [balance,      setBalance]      = useState(null);
  const [stopLosses,   setStopLosses]   = useState({});
  const [takeProfits,  setTakeProfits]  = useState({});
  const [sentiment,    setSentiment]    = useState(null);
  const [histories,    setHistories]    = useState({});
  const [lastUpd,      setLastUpd]      = useState("");

  // ── Config state ──
  const [orderSize,    setOrderSize]    = useState(10);
  const [minConf,      setMinConf]      = useState(75);
  const [stopLossPct,  setStopLossPct]  = useState(3);
  const [takeProfitPct,setTakeProfitPct]= useState(6);
  const [maxPositions, setMaxPositions] = useState(2);
  const [intervalMin,  setIntervalMin]  = useState(60);
  const [maxDailyDrawdown, setMaxDailyDrawdown] = useState(8);
  const [showAllLogs,  setShowAllLogs]  = useState(false);
  const [logFilter,    setLogFilter]    = useState("all");
  const [newCoinSym,   setNewCoinSym]   = useState("");
  const [coinMsg,      setCoinMsg]      = useState("");
  const [addingCoin,   setAddingCoin]   = useState(false);
  const [btcTrend,     setBtcTrend]     = useState(null);
  const [nextRun,      setNextRun]      = useState(null);
  const [hourlyPerf,   setHourlyPerf]   = useState(null);
  const [marketSafe,   setMarketSafe]   = useState(true);
  const [riskMult,     setRiskMult]     = useState(1.0);
  const [toast,        setToast]        = useState(null); // {msg, type, ts}

  // ── Refs ──
  const priceRef    = useRef({});
  const coinListRef  = useRef(DEFAULT_COINS);
  const syncCountRef  = useRef(0);
  const autoRef   = useRef(null);

  // ── Helpers ──
  const addLog = useCallback((msg, type = "info") => {
    const entry = {
      msg, type,
      time: new Date().toLocaleTimeString("es-ES", { timeZone: "Europe/Madrid" }),
      date: new Date().toLocaleDateString("es-ES", { timeZone: "Europe/Madrid" }),
      ts:   Date.now()
    };
    setLogs(prev => {
      const next = [entry, ...prev.slice(0, 299)];
      try { localStorage.setItem(LOGS_KEY, JSON.stringify(next.slice(0, 200))); } catch(_) {}
      return next;
    });
    // Toast visual para compras y ventas
    if (type === "ok" && (msg.includes("COMPRA ") || msg.includes("VENTA "))) {
      const isBuy = msg.includes("COMPRA ");
      setToast({ msg: msg.slice(0, 60), type: isBuy ? "buy" : "sell", ts: Date.now() });
      setTimeout(() => setToast(null), 5000);
    }
    // Notificacion push en compras y ventas
    if ((type === "ok" || type === "error") && (msg.includes("COMPRA") || msg.includes("VENTA") || msg.includes("STOP") || msg.includes("PROFIT"))) {
      sendNotification("Bitunix Bot", msg);
    }
  }, []);

  const saveOhlcCache = (data) => { try { sessionStorage.setItem(OHLC_CACHE_KEY, JSON.stringify(data)); } catch(_) {} };

  // ── Proxy check ──
  useEffect(() => {
    const checkProxy = () =>
      fetch(`${CONFIG.PROXY}/health`)
        .then(r => r.json())
        .then(d => { setProxyOk(true); if (d.riskMult !== undefined) setRiskMult(d.riskMult || 1.0); })
        .catch(() => setProxyOk(false));
    checkProxy();
    const iv = setInterval(checkProxy, 30000);
    return () => clearInterval(iv);
  }, []);

  // ── Sync from server ──
  const syncFromServer = useCallback(async () => {
    try {
      const data = await apiGet("/api/state");
      if (!data || data.error) {
        addLog(`❌ Sync fallido: ${data?.error || "Sin respuesta del servidor"}`, "error");
        setProxyOk(false);
        return;
      }
      setProxyOk(true);
      // Monedas del servidor
      if (data.coins?.length > 0) {
        setCoinList(data.coins);
        coinListRef.current = data.coins;
      }
      // Precios del servidor (solo como fallback si no tenemos del WS)
      // El merge real se hace más abajo
      // Análisis: usar los más recientes entre servidor y local
      // Análisis: el servidor es la fuente de verdad
      if (data.analyses && Object.keys(data.analyses).length > 0) {
        setAllAnalyses(data.analyses);
        // Indicadores frescos del servidor
        const freshInd = {};
        Object.entries(data.analyses).forEach(([id, a]) => {
          if (a.indicators) freshInd[id] = a.indicators;
        });
        if (Object.keys(freshInd).length > 0) setIndicators(freshInd);
      }
      if (data.trades?.length)  setTrades(data.trades);
      // Logs: gestionados por el polling rápido a /api/logs (cada 5s, incremental)
      // No los actualizamos aquí para evitar pisar los logs nuevos del polling
      // if (data.logs?.length) { setLogs([...data.logs].slice(0, 300)); }
      // Precios: usar directamente del servidor sin mezclar con datos viejos
      if (data.prices && Object.keys(data.prices).length > 0) {
        setPrices(data.prices);
      }
      if (data.changes && Object.keys(data.changes).length > 0) {
        setChanges(data.changes);
      }
      if (data.openOrders) {
        const sl = {}, tp = {};
        Object.entries(data.openOrders).forEach(([id, o]) => {
          // Ignorar posiciones pending y extras (_extra suffix)
          if (o.qty === "pending" || id.endsWith("_extra")) return;
          if (true) {
            // Guardar todos los campos necesarios: price para compatibilidad, sl/tp/entryPrice para el gráfico
            sl[id] = {
              price:      o.sl,        // compatibilidad con componentes que usan sl.price
              sl:         o.sl,        // para el gráfico MiniChart
              tp:         o.tp,
              tp2:        o.tp2,
              tp3:        o.tp3,
              qty:        o.qty,
              symbol:     o.symbol,
              pair:       o.pair,
              entryPrice: o.entryPrice,
              averaged:   o.averaged,
              tpHits:     o.tpHits||0,
              partialSold:o.partialSold,
              openTime:   o.openTime,
            };
            tp[id] = { price: o.tp, qty: o.qty, symbol: o.symbol, pair: o.pair, entryPrice: o.entryPrice };
          }
        });
        setStopLosses(sl); setTakeProfits(tp);
      }
      if (data.running    !== undefined) setAutoMode(data.running);
      // data.coins ya fue procesado arriba — no duplicar
      if (data.btcTrend   !== undefined) setBtcTrend(data.btcTrend);
      if (data.marketSafe !== undefined) setMarketSafe(data.marketSafe);
      // Cargar saldo cada 5 syncs (~75s) para no sobrecargar API Bitunix
      syncCountRef.current = (syncCountRef.current || 0) + 1;
      if (syncCountRef.current % 5 === 1) {
        try {
          const bal = await apiGet("/api/balance");
          if (bal?.balance?.length > 0) {
            setBalance(bal.balance);
          }
          // Usar totales directos si los devuelve el servidor
          if (bal?.total > 0) {
            // El servidor ya calculó el total — no hay que recalcular
          }
        } catch (_) {}
      }
      if (data.config) {
        if (data.config.paperMode        !== undefined) setPaperMode(data.config.paperMode);
        if (data.config.orderSize        !== undefined) setOrderSize(data.config.orderSize);
        if (data.config.minConfidence    !== undefined) setMinConf(data.config.minConfidence);
        if (data.config.stopLossPct      !== undefined) setStopLossPct(data.config.stopLossPct);
        if (data.config.takeProfitPct    !== undefined) setTakeProfitPct(data.config.takeProfitPct);
        if (data.config.maxOpenPositions !== undefined) setMaxPositions(data.config.maxOpenPositions);
        if (data.config.intervalMinutes  !== undefined) setIntervalMin(data.config.intervalMinutes);
        if (data.config.maxDailyDrawdown !== undefined) setMaxDailyDrawdown(data.config.maxDailyDrawdown);
      }
      if (data.fearGreed)    setFearGreed(data.fearGreed);
      if (data.globalRegime) setGlobalRegime(data.globalRegime);
      if (data.config?.trailingStop    !== undefined) setTrailingStop(data.config.trailingStop);
      if (data.config?.sentimentFilter !== undefined) setSentimentFilter(data.config.sentimentFilter);
      if (data.config?.dynamicRisk     !== undefined) setDynamicRisk(data.config.dynamicRisk);
      if (data.config?.volumeFilter    !== undefined) setVolumeFilter(data.config.volumeFilter);
      if (data.fundingRates) setFundingRates(data.fundingRates);
      if (data.botStats)     setBotStats(data.botStats);
      if (data.equity?.length > 0) setEquityData(data.equity);
      if (data.paperStats)   setPaperStats(data.paperStats);
      if (data.nextRun)      setNextRun(data.nextRun);
      if (data.hourlyPerf)   setHourlyPerf(data.hourlyPerf);
      if (data.riskMultiplier !== undefined) setRiskMult(data.riskMultiplier);
    } catch (_) {}
  }, []);

  // ── Fetch prices ──
  const fetchPrices = useCallback(async () => {
    const newPrices = {}, newChanges = {};
    const results = await Promise.all(coinListRef.current.map(c =>
      fetch(`${CONFIG.PROXY}/api/bitunix/ticker?symbol=${c.pair}`)
        .then(r => r.json()).catch(() => null)
    ));
    const curCoins = coinListRef.current;
    results.forEach((r, i) => {
      const c = curCoins[i];
      if (!c) return;
      if (r && (String(r.code) === "0" || r.code === 0) && r.data) {
        const _p = parseFloat(r.data.lastPrice || r.data.price || 0);
        if (_p > 0) {
          newPrices[c.id]  = _p;
          newChanges[c.id] = parseFloat(r.data.priceChange24h || r.data.change24h || 0);
        }
      }
    });
    if (Object.keys(newPrices).length > 0) {
      setPrices(prev => ({ ...prev, ...newPrices }));
      priceRef.current = { ...priceRef.current, ...newPrices };
      // NO actualizamos changes desde /api/bitunix/ticker: ese endpoint devuelve
      // priceChange24h:"0" hardcodeado y sobrescribiría el valor real que viene
      // de /api/state (calculado desde OHLC en el backend).
      // setChanges(prev => ({ ...prev, ...newChanges }));
      setHistories(prev => {
        const next = { ...prev };
        coinListRef.current.forEach(c => {
          if (newPrices[c.id]) {
            next[c.id] = [...(prev[c.id] || []).slice(-59), newPrices[c.id]];
          }
        });
        return next;
      });
      setLastUpd(new Date().toLocaleTimeString());
      setProxyOk(true);
    }
  }, []);

  // ── Fetch real OHLC history ──
  const fetchRealHistory = useCallback(async (coin) => {
    try {
      const data = await apiGet(`/api/klines?symbol=${coin.pair}&interval=1h&limit=100`);
      if (!data || data.error) return null;
      if (data.closes?.length > 10) {
        const closes = data.closes;
        const ohlc   = data.ohlc || [];
        // Actualizar histories e indicators inmediatamente
        setHistories(prev => ({ ...prev, [coin.id]: closes }));
        // Solo actualizar indicators si el servidor no tiene datos frescos para esta moneda
        setIndicators(prev => {
          if (prev[coin.id]?.adx) return prev; // servidor ya tiene indicadores completos
          const ind = getIndicators(closes, ohlc);
          return { ...prev, [coin.id]: ind };
        });
        // Guardar OHLC
        setOhlcData(prev => {
          const next = { ...prev, [coin.id]: data };
          try { sessionStorage.setItem(OHLC_CACHE_KEY, JSON.stringify(next)); } catch(_) {}
          return next;
        });
        return { closes, ohlc };
      }
    } catch (_) {}
    return null;
  }, [token]);

  // ── Refresh balance ──
  const refreshBalance = useCallback(async () => {
    try {
      const data = await apiGet("/api/balance");
      if (data.balance?.length > 0) setBalance(data.balance);
    } catch (_) {}
  }, []);

  // ── Analysis ──
  const runAnalysis = useCallback(async (target = coin) => {
    if (analyzing) return;
    setAnalyzing(true);
    // Timeout de seguridad — si falla algo, desbloquear en 30s
    const _safeTimeout = setTimeout(() => setAnalyzing(false), 30000);
    try {
      // Solo analizar — las órdenes las ejecuta el servidor en runBotCycle
      const data = await apiPost("/api/analyze-coin-full", { coinId: target.id }, 45000);

      let parsed;
      if (data?.analysis) {
        parsed = data.analysis;
      } else {
        const state = await apiGet("/api/state");
        parsed = state.analyses?.[target.id] || { action:"HOLD", confidence:0, reasoning:"Sin datos", key_factors:[], risk_level:"MEDIUM", time_horizon:"SHORT" };
      }

      if (!["BUY","SELL","HOLD"].includes((parsed.action||"").toUpperCase())) parsed.action = "HOLD";
      parsed.action    = parsed.action.toUpperCase();
      parsed.timestamp = Date.now();
      parsed.coin      = target.symbol;

      setAnalysis(parsed);
      setAllAnalyses(prev => {
        const next = { ...prev, [target.id]: { ...parsed, symbol: target.symbol, timestamp: Date.now() } };
        try { localStorage.setItem(ANALYSES_KEY, JSON.stringify(next)); } catch(_) {}
        return next;
      });
      addLog(`${target.symbol}: ${parsed.action} (${parsed.confidence}%) — ${parsed.reasoning?.slice(0,60)||""}`, parsed.action === "BUY" ? "ok" : "info");
      // NOTA: Las órdenes las ejecuta runBotCycle automáticamente
      // El botón solo muestra el análisis, nunca ejecuta órdenes directamente
    } catch(e) { addLog(`Error análisis: ${e.message}`, "error"); }
    clearTimeout(_safeTimeout);
    setAnalyzing(false);
  }, [analyzing, addLog]);

  const runAllAnalysis = useCallback(async () => {
    if (analyzingAll) return;
    const list = coinListRef.current?.length > 0 ? [...coinListRef.current] : [...DEFAULT_COINS];
    if (list.length === 0) { addLog("Sin monedas para analizar", "warn"); return; }
    setAnalyzingAll(true);
    let buys = 0, holds = 0;
    for (let i = 0; i < list.length; i++) {
      const c = list[i];
      setAllProgress(`${i+1}/${list.length} ${c.symbol}`);
      try {
        const data = await apiPost("/api/analyze-coin-full", { coinId: c.id }, 45000);
        const parsed = data?.analysis;
        if (parsed) {
          const action = (parsed.action||"HOLD").toUpperCase();
          if (action === "BUY") buys++; else holds++;
          setAllAnalyses(prev => ({ ...prev, [c.id]: { ...parsed, symbol: c.symbol, timestamp: Date.now() } }));
        } else if (data?.error) {
          addLog(`${c.symbol}: ${data.error}`, "warn");
        }
      } catch(e) { addLog(`Error análisis ${c.symbol}: ${e.message}`, "error"); }
      if (i < list.length - 1) await new Promise(r => setTimeout(r, 1500));
    }
    setAllProgress("");
    setAnalyzingAll(false);
    addLog(`Análisis completo: ${buys} BUY · ${holds} HOLD/SELL`, "ok");
  }, [analyzingAll, addLog]);

  // ── Effects ──
  useEffect(() => { coinListRef.current = coinList; }, [coinList]);
  // Precios cargados al inicio y luego por el efecto de sync
  // fetchPrices al inicio — el interval está en el efecto de sync
  useEffect(() => { refreshBalance(); const iv = setInterval(refreshBalance, 60000); return () => clearInterval(iv); }, [refreshBalance]);
  useEffect(() => {
    syncFromServer();
    // Sync estado completo cada 15s (estado, trades, análisis, etc.)
    const syncIv = setInterval(syncFromServer, 15000);
    // Precios REST cada 10s solo como fallback (WS es el principal)
    const priceIv = setInterval(fetchPrices, 10000);
    return () => { clearInterval(syncIv); clearInterval(priceIv); };
  }, [syncFromServer, fetchPrices]);

  // Polling rápido SOLO de logs cada 5s — payload mínimo, refresco frecuente
  // Usa ?since=<ts> para traer solo logs nuevos (incremental)
  useEffect(() => {
    let lastTs = 0;
    let stopped = false;
    let failCount = 0;
    const tick = async () => {
      if (stopped) return;
      // Si hay errores consecutivos (ej. token inválido), reducir frecuencia a 30s
      if (failCount >= 3 && failCount % 6 !== 0) { failCount++; return; }
      try {
        const url = lastTs > 0 ? `/api/logs?since=${lastTs}` : `/api/logs`;
        const data = await apiGet(url);
        if (data?.error) { failCount++; return; }
        failCount = 0;
        if (data?.logs?.length > 0) {
          if (lastTs === 0) {
            // Primera carga: reemplazar todos
            setLogs(data.logs.slice(0, 300));
          } else {
            // Carga incremental: prepend solo los nuevos
            setLogs(prev => {
              const newOnes = data.logs.filter(l => l.ts > lastTs);
              if (newOnes.length === 0) return prev;
              return [...newOnes, ...prev].slice(0, 300);
            });
          }
          lastTs = Math.max(lastTs, ...data.logs.map(l => l.ts || 0));
        } else if (data?.serverTs && lastTs === 0) {
          lastTs = data.serverTs;
        }
      } catch (_) { failCount++; }
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => { stopped = true; clearInterval(iv); };
  }, []);
  useEffect(() => {
    const loadHistories = async () => {
      // Primero cargar monedas del servidor
      try {
        const d = await apiGet("/api/coins");
        if (d.coins?.length > 0) {
          setCoinList(d.coins);
          coinListRef.current = d.coins;
        }
      } catch(_) {}
      // Pequeño delay para que coinListRef se actualice
      await new Promise(r => setTimeout(r, 500));
      // Sin caché — solo datos frescos del servidor
      const next = {}, nextOhlc = {}, ind = {};
      for (const c of coinListRef.current) {
        const r = await fetchRealHistory(c);
        if (r?.closes?.length > 10) { next[c.id] = r.closes; nextOhlc[c.id] = r; ind[c.id] = getIndicators(r.closes, r.ohlc||[]); }
        await new Promise(r2 => setTimeout(r2, 500));
      }
      if (Object.keys(next).length > 0) { setHistories(next); setOhlcData(nextOhlc); setIndicators(ind); }
    };
    loadHistories();
    const iv = setInterval(loadHistories, 5*60*1000);
    return () => clearInterval(iv);
  }, [fetchRealHistory]);

  // ── Derived values ──
  const findAsset   = (bal, sym) => bal?.find?.(b => (b.coin||"").toUpperCase() === sym.toUpperCase());
  const getAvail    = (a) => Math.max(0, parseFloat(a?.balance ?? 0) - parseFloat(a?.balanceLocked ?? 0));
  const usdtBal     = getAvail(findAsset(balance, "USDT"));
  const usdcBal     = getAvail(findAsset(balance, "USDC"));
  // Total portfolio = USDT + USDC + valor de todas las monedas al precio actual
  const totalBalanceUSD = balance ? balance.reduce((sum, a) => {
    const sym   = (a.coin || "").toUpperCase();
    const avail = Math.max(0, parseFloat(a.balance||0) - parseFloat(a.balanceLocked||0));
    const c     = COINS.find(x => x.symbol === sym);
    const p     = c ? (prices[c.id] || 0) : (sym === "USDT" || sym === "USDC" ? 1 : 0);
    return sum + avail * p;
  }, 0) : (usdtBal + usdcBal);
  const closedTrades    = trades.filter(t => t.pnl != null && t.action === "SELL");
  const totalFeesUSD    = trades.reduce((s,t) => s+(t.fees||t.fee||0), 0);
  const totalNetPct     = closedTrades.reduce((s,t) => s+(parseFloat(t.pnl)||0), 0);
  const winsNet         = closedTrades.filter(t => parseFloat(t.pnl) >= 0).length;
  const lossesNet       = closedTrades.filter(t => parseFloat(t.pnl) < 0).length;
  const winRateNet      = closedTrades.length > 0 ? Math.round(winsNet/closedTrades.length*100) : null;
  const curIndic        = indicators[coin.id];
  const hasSL           = !!stopLosses[coin.id];
  const hasTP           = !!takeProfits[coin.id];

  const tabStyle = (t) => ({
    padding: "12px 14px", fontSize: 10, fontFamily: "'Space Mono',monospace",
    cursor: "pointer", border: "none", borderBottom: `2px solid ${activeTab === t ? "#00d4a0" : "transparent"}`,
    background: activeTab === t ? "rgba(0,255,170,0.04)" : "transparent",
    color: activeTab === t ? "#00d4a0" : "#7a8899",
    transition: "all 0.2s", letterSpacing: 1, flex: 1, minHeight: 44,
  });

  return (
    <div style={{ minHeight:"100vh", background:"#07090d", color:"#e0e0e0", fontFamily:"'Space Mono',monospace", display:"flex", flexDirection:"column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Inter:wght@400;500;600;700&display=swap');

        /* ── BASE ── */
        *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;margin:0;padding:0}
        html{font-size:16px}
        body{margin:0;background:#07090d;overscroll-behavior:none}

        /* ── SCROLLBARS ── */
        ::-webkit-scrollbar{width:3px;height:3px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:2px}
        ::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.15)}

        /* ── ANIMACIONES ── */
        @keyframes pulse-dot{0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(0,212,160,0.5)}50%{opacity:.8;box-shadow:0 0 0 6px rgba(0,212,160,0)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateX(4px)}to{opacity:1;transform:translateX(0)}}

        /* ── ELEMENTOS BASE ── */
        input[type=range]{height:4px;border-radius:2px;cursor:pointer;outline:none}
        button{touch-action:manipulation;font-family:inherit}
        .live-dot{animation:pulse-dot 2s infinite}
        .sigcard-hover:hover{transform:translateY(-1px);transition:transform .15s}
        .coin-btn-hover:hover{background:rgba(255,255,255,0.03)!important}
        .tab-btn{transition:all 0.15s}
        .tab-btn:hover{color:#00d4a0!important}

        /* ══════════════════════════════════════
           iPhone 15 Pro (393×852px, 3x DPR)
           Safari mobile, viewport estrecho
           ══════════════════════════════════════ */
        @media(max-width:430px){
          html{font-size:14px}
          /* Layout en columna — sidebar debajo del contenido */
          .main-full{
            grid-template-columns:1fr!important;
            height:auto!important;
            overflow:visible!important;
          }
          .sidebar-hide{
            display:flex!important;
            flex-direction:column!important;
            border-left:none!important;
            border-top:1px solid rgba(255,255,255,0.05)!important;
            width:100%!important;
            max-height:none!important;
          }
          /* Tabs más grandes para dedos */
          .tab-btn{
            font-size:10px!important;
            padding:14px 4px!important;
            min-height:52px!important;
            letter-spacing:1px!important;
          }
          /* Grid señales 2 columnas en móvil */
          .sig-grid{grid-template-columns:1fr 1fr!important}
          /* Topbar compacto */
          .topbar-badges{display:none!important}
          .topbar-title{font-size:13px!important}
          /* Coin list 2 columnas */
          .coin-grid{grid-template-columns:1fr 1fr!important}
          /* Botones más grandes para tactil */
          .btn-start{padding:14px 8px!important;font-size:11px!important}
          /* Portfolio más compacto */
          .portfolio-grid{grid-template-columns:1fr 1fr!important}
          /* Texto mínimo legible en iPhone */
          .sb-label{font-size:9px!important}
          .sb-val{font-size:13px!important}
          /* Config sliders más fáciles de usar */
          input[type=range]{height:6px!important}
        }

        /* ══════════════════════════════════════
           Tablet / iPhone landscape (431-767px)
           ══════════════════════════════════════ */
        @media(min-width:431px) and (max-width:767px){
          .main-full{grid-template-columns:1fr!important;height:auto!important}
          .sidebar-hide{display:flex!important;flex-direction:column!important;border-left:none!important;border-top:1px solid rgba(255,255,255,0.05)!important}
          .tab-btn{font-size:11px!important;padding:13px 8px!important;min-height:50px!important}
          .sig-grid{grid-template-columns:1fr 1fr 1fr!important}
          .topbar-badges{display:none!important}
        }

        /* ══════════════════════════════════════
           MacBook 13" (1280×800px, 2x DPR)
           Safari/Chrome desktop, pantalla compacta
           ══════════════════════════════════════ */
        @media(min-width:768px) and (max-width:1366px){
          html{font-size:15px}
          .main-full{grid-template-columns:1fr 290px!important}
          .sidebar-hide{display:flex!important;flex-direction:column!important}
          .tab-btn{font-size:11px!important;padding:11px 10px!important;min-height:44px!important}
          .sig-grid{grid-template-columns:repeat(2,1fr)!important}
          .sb-label{font-size:9px!important}
          .sb-val{font-size:14px!important}
          .coin-grid{grid-template-columns:1fr 1fr!important}
          .portfolio-grid{grid-template-columns:repeat(4,1fr)!important}
        }

        /* ══════════════════════════════════════
           Monitor grande 1440px+
           ══════════════════════════════════════ */
        @media(min-width:1367px){
          .main-full{grid-template-columns:1fr 320px!important}
          .sidebar-hide{display:flex!important;flex-direction:column!important}
          .tab-btn{font-size:12px!important;padding:12px 14px!important}
          .sig-grid{grid-template-columns:repeat(3,1fr)!important}
          .coin-grid{grid-template-columns:1fr 1fr 1fr!important}
          .portfolio-grid{grid-template-columns:repeat(4,1fr)!important}
          .sb-label{font-size:10px!important}
          .sb-val{font-size:15px!important}
        }
      `}</style>

      {/* TOP BAR */}
      <div style={{ background:"#0d1117", borderBottom:"1px solid rgba(255,255,255,0.05)", padding:"9px 14px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div className="live-dot" style={{ width:8, height:8, background:"#00d4a0", borderRadius:"50%", flexShrink:0 }}/>
          <div>
            <div className="topbar-title" style={{ fontSize:14, fontWeight:700, letterSpacing:3, color:"#e8eaf0" }}>BITUNIX BOT</div>
            <div style={{ fontSize:8, color:"#4a5568", letterSpacing:1 }}>v9.0-elite · AI POWERED</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:5, alignItems:"center", flexWrap:"wrap" }}>
          {[
            { label: proxyOk ? "● ONLINE" : "● OFFLINE", cls: proxyOk ? "g" : "r" },
            { label: autoMode ? "● ACTIVO" : "○ PARADO", cls: autoMode ? "g" : "n" },
            ...(btcTrend ? [{ label:`BTC ${btcTrend?.change1h>=0?"+":""}${btcTrend?.change1h?.toFixed(1)}% ${marketSafe?"✓":"⚠"}`, cls: marketSafe?"g":"r" }] : []),
            { label: paperMode ? "PAPER" : "⚡ REAL", cls: paperMode ? "n" : "r" },
          ].map((b,i) => (
            <span key={i} style={{ fontSize:9, padding:"3px 9px", borderRadius:20, border:"1px solid", background:b.cls==="g"?"rgba(0,212,160,0.08)":b.cls==="r"?"rgba(255,68,102,0.1)":"rgba(255,255,255,0.03)", color:b.cls==="g"?"#00d4a0":b.cls==="r"?"#ff4466":"#8892a4", borderColor:b.cls==="g"?"rgba(0,212,160,0.2)":b.cls==="r"?"rgba(255,68,102,0.2)":"rgba(255,255,255,0.06)" }}>{b.label}</span>
          ))}
          <div style={{ textAlign:"right", marginLeft:4 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#e8eaf0", fontFamily:"monospace" }}>${totalBalanceUSD > 0 ? totalBalanceUSD.toFixed(2) : (usdtBal+usdcBal).toFixed(2)}</div>
            <div style={{ display:"flex", gap:6, justifyContent:"flex-end", alignItems:"center" }}>
              <span style={{ fontSize:8, color:"#4a5568" }}>Portfolio</span>
              {(() => {
                const todayPnl = closedTrades
                  .filter(t => t.date === new Date().toLocaleDateString("es-ES",{timeZone:"Europe/Madrid"}))
                  .reduce((s,t) => s + parseFloat(t.pnl||0), 0);
                return todayPnl !== 0 ? (
                  <span style={{ fontSize:8, fontFamily:"monospace", color:todayPnl>=0?"#00d4a0":"#ff4466" }}>
                    {todayPnl>=0?"+":""}{todayPnl.toFixed(2)}% hoy
                  </span>
                ) : null;
              })()}
            </div>
          </div>
          <button onClick={onLogout} style={{ fontSize:9, padding:"4px 10px", borderRadius:6, background:"rgba(255,255,255,0.06)", color:"#c0c8d8", border:"1px solid rgba(255,255,255,0.15)", cursor:"pointer", fontFamily:"monospace" }}>SALIR</button>
        </div>
      </div>

      {/* COIN STRIP */}
      <div style={{ background:"#0a0e14", borderBottom:"1px solid rgba(255,255,255,0.05)", display:"flex", overflowX:"auto", flexShrink:0, scrollbarWidth:"none" }}>
        <style>{`
.coin-strip::-webkit-scrollbar{display:none}
@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
`}</style>
        {coinList.map(c => {
          const ch   = changes[c.id] || 0;
          const isSel = coin.id === c.id;
          const hasSL = !!stopLosses[c.id];
          const rsi   = indicators[c.id]?.rsi;
          const pnl   = hasSL && stopLosses[c.id]?.entryPrice > 0 && prices[c.id]
            ? ((prices[c.id] - stopLosses[c.id].entryPrice) / stopLosses[c.id].entryPrice * 100)
            : null;
          return (
            <button key={c.id} className="coin-btn-hover" onClick={() => setCoin(c)} style={{
              padding:"6px 10px", border:"none",
              borderBottom:`2px solid ${isSel?"#00d4a0":hasSL?(pnl>=0?"rgba(0,212,160,0.3)":"rgba(255,68,102,0.3)"):"transparent"}`,
              background:isSel?"rgba(0,212,160,0.05)":"transparent",
              cursor:"pointer", minWidth:70, position:"relative", flexShrink:0, textAlign:"center"
            }}>
              {hasSL && (
                <span style={{ position:"absolute", top:3, right:3, fontSize:6,
                  color:pnl!=null?(pnl>=0?"#00d4a0":"#ff4466"):"#f5a623" }}>●</span>
              )}
              <div style={{ fontSize:9, fontWeight:700, color:isSel?"#00d4a0":"#8892a4", marginBottom:1, letterSpacing:.5 }}>{c.symbol}</div>
              <div style={{ fontSize:12, fontWeight:700, color:"#e8eaf0", fontFamily:"monospace" }}>
                {prices[c.id] >= 100 ? `$${prices[c.id]?.toFixed(0)}` :
                 prices[c.id] >= 1   ? `$${prices[c.id]?.toFixed(2)}` :
                 prices[c.id] >= 0.01? `$${prices[c.id]?.toFixed(4)}` :
                                       `$${prices[c.id]?.toFixed(5)}`}
              </div>
              <div style={{ fontSize:8, fontFamily:"monospace", fontWeight:600,
                color:ch>=0?"#00d4a0":"#ff4466" }}>{ch>=0?"+":""}{ch.toFixed(2)}%</div>
              {pnl != null && (
                <div style={{ fontSize:7, fontFamily:"monospace",
                  color:pnl>=0?"#00d4a0":"#ff4466", marginTop:1 }}>{pnl>=0?"+":""}{pnl.toFixed(1)}%</div>
              )}
            </button>
          );
        })}
      </div>

      {/* MAIN GRID */}
      <div className="main-full" style={{ display:"grid", gridTemplateColumns:"1fr 280px", flex:1, overflow:"hidden" }}>

        {/* LEFT COLUMN */}
        <div style={{ display:"flex", flexDirection:"column", overflow:"hidden" }}>

          {/* TABS DE NAVEGACIÓN */}
          <div style={{ display:"flex", borderBottom:"1px solid rgba(255,255,255,0.06)", background:"#0a0e14", flexShrink:0 }}>
            {[
              { id:"chart",   label:"📈 GRÁFICO" },
              { id:"signals", label:"⚡ SEÑALES" },
              { id:"trades",  label:"📋 TRADES"  },
              { id:"stats",   label:"📊 STATS"   },
              { id:"config",  label:"⚙️ CONFIG"  },
              { id:"logs",    label:"🗒 LOGS"    },
            ].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                flex:1, padding:"10px 4px", background:"transparent", border:"none",
                borderBottom:`2px solid ${activeTab===t.id?"#00d4a0":"transparent"}`,
                color: activeTab===t.id?"#00d4a0":"#4a5568",
                fontFamily:"monospace", fontSize:9, cursor:"pointer", letterSpacing:.5,
                transition:"all 0.15s"
              }}>{t.label}</button>
            ))}
          </div>

          {/* TAB CONTENT */}
          <div style={{ flex:1, overflowY:"auto", background:"#0d1117" }}>

            {/* GRÁFICO */}
            {activeTab==="chart" && (
              <div style={{ height:"calc(100vh - 160px)", minHeight:400 }}>
                <TradingChart
                  coin={coin}
                  stopLosses={stopLosses}
                  trades={trades}
                  prices={prices}
                />
              </div>
            )}

            {/* SEÑALES */}
            {activeTab==="signals" && (
              <div style={{ padding:"12px 14px", animation:"fadeIn 0.2s" }}>
                <div className="sig-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:6, marginBottom:10 }}>
                  {COINS.map(c => {
                    const a = allAnalyses[c.id];
                    const clr = a?.action==="BUY"?"#00d4a0":a?.action==="SELL"?"#ff4466":"#4a5568";
                    const bg  = a?.action==="BUY"?"rgba(0,212,160,0.05)":a?.action==="SELL"?"rgba(255,68,102,0.05)":"rgba(255,255,255,0.02)";
                    const br  = a?.action==="BUY"?"rgba(0,212,160,0.2)":a?.action==="SELL"?"rgba(255,68,102,0.2)":"rgba(255,255,255,0.05)";
                    const sc  = a?.confidence || 0;
                    return (
                      <div key={c.id} className="sigcard-hover" onClick={() => { setCoin(c); setAnalysis(a); }} style={{ padding:"10px", borderRadius:8, border:`1px solid ${br}`, background:bg, cursor:"pointer", transition:"all 0.15s" }}>
                        {/* Header */}
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <span style={{ fontSize:11, fontWeight:700, color:"#e8eaf0", fontFamily:"'Inter',sans-serif" }}>{c.symbol}</span>
                            {stopLosses[c.id] && (
                              <span style={{ fontSize:6, padding:"1px 3px", borderRadius:2,
                                background: ((prices[c.id]||0)-(stopLosses[c.id]?.entryPrice||0))/(stopLosses[c.id]?.entryPrice||1)*100 >= 0 ? "rgba(0,212,160,0.15)" : "rgba(255,68,102,0.15)",
                                color: ((prices[c.id]||0)-(stopLosses[c.id]?.entryPrice||0))/(stopLosses[c.id]?.entryPrice||1)*100 >= 0 ? "#00d4a0" : "#ff4466"
                              }}>
                                {(((prices[c.id]||0)-(stopLosses[c.id]?.entryPrice||0))/(stopLosses[c.id]?.entryPrice||1)*100)>=0?"+":""}
                                {(((prices[c.id]||0)-(stopLosses[c.id]?.entryPrice||0))/(stopLosses[c.id]?.entryPrice||1)*100).toFixed(1)}%
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize:9, fontWeight:700, color:clr, letterSpacing:.5 }}>{a?.action||"—"}</span>
                        </div>
                        {/* Score bar */}
                        <div style={{ height:3, background:"rgba(255,255,255,0.05)", borderRadius:2, marginBottom:5, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${sc}%`, background:clr, borderRadius:2, transition:"width 0.5s" }}/>
                        </div>
                        {/* Indicadores clave */}
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:2 }}>
                          <div style={{ fontSize:7, color:"#6b7a8d" }}>Score: <span style={{ color:sc>=80?"#00d4a0":sc>=65?"#f5a623":"#a0aab8" }}>{a?.scoring?.score ?? sc}</span></div>
                          <div style={{ fontSize:7, color:"#6b7a8d" }}>RSI: <span style={{ color:(a?.indicators?.rsi??indicators[c.id]?.rsi??50)<30?"#00d4a0":(a?.indicators?.rsi??indicators[c.id]?.rsi??50)>70?"#ff4466":"#a0aab8" }}>{a?.indicators?.rsi ?? indicators[c.id]?.rsi ?? "—"}</span></div>
                          <div style={{ fontSize:7, color:"#6b7a8d" }}>ADX: <span style={{ color:(a?.indicators?.adx?.adx??indicators[c.id]?.adx?.adx??0)>25?"#f5a623":"#a0aab8" }}>{(a?.indicators?.adx?.adx ?? indicators[c.id]?.adx?.adx)?.toFixed(0) ?? "—"}</span></div>
                          <div style={{ fontSize:7, color:"#6b7a8d" }}>Vol: <span style={{ color:(a?.indicators?.vol?.ratio??indicators[c.id]?.vol?.ratio??0)>1.5?"#f5a623":"#a0aab8" }}>{(a?.indicators?.vol?.ratio ?? indicators[c.id]?.vol?.ratio)?.toFixed(1) ?? "—"}x</span></div>
                        </div>
                        {a?.timestamp && <div style={{ fontSize:7, color:"#5a6478", marginTop:4, textAlign:"right" }}>{typeof a.timestamp === "number" ? new Date(a.timestamp).toLocaleTimeString("es-ES",{hour:"2-digit",minute:"2-digit"}) : a.timestamp}</div>}
                      </div>
                    );
                  })}
                </div>
                {analysis && (
                  <div style={{ background:"rgba(77,166,255,0.05)", border:"1px solid rgba(77,166,255,0.12)", borderRadius:8, padding:10, marginBottom:10, animation:"fadeIn 0.2s" }}>
                    <div style={{ fontSize:8, color:"#4da6ff", letterSpacing:2, marginBottom:6 }}>IA · {analysis.coin || coin.symbol}</div>
                    <div style={{ fontSize:9, color:"#b0bac8", lineHeight:1.8 }}>{analysis.reasoning}</div>
                    <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginTop:6 }}>
                      {analysis.key_factors?.map((f,i) => (
                        <span key={i} style={{ fontSize:8, padding:"2px 6px", borderRadius:6, background:"rgba(77,166,255,0.08)", color:"#4da6ff", border:"1px solid rgba(77,166,255,0.12)" }}>{f}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:5 }}>
                  {[
                    { l:"RSI 14",    v:curIndic?.rsi ?? "—",          c:curIndic?.rsi>70?"#ff4466":curIndic?.rsi<30?"#00d4a0":"#f5a623" },
                    { l:"MACD",      v:curIndic?.macdHist>0?"BULL":"BEAR", c:curIndic?.macdHist>0?"#00d4a0":"#ff4466" },
                    { l:"BB POS",    v:curIndic?.bbPos ? `${curIndic.bbPos}%` : "—", c:"#8892a4" },
                    { l:"VOLUMEN",   v:curIndic?.vol?.ratio ? `${curIndic.vol.ratio.toFixed(1)}x` : "—", c:curIndic?.vol?.ratio>1.5?"#00d4a0":curIndic?.vol?.ratio<0.8?"#ff4466":"#8892a4" },
                    { l:"TENDENCIA", v:curIndic?.trend || "—", c:curIndic?.trend==="ALCISTA"?"#00d4a0":curIndic?.trend==="BAJISTA"?"#ff4466":"#8892a4" },
                    { l:"DIV RSI",   v:curIndic?.divergence?.bullish?"↑ ALC":curIndic?.divergence?.bearish?"↓ BAJ":"—", c:curIndic?.divergence?.bullish?"#00d4a0":curIndic?.divergence?.bearish?"#ff4466":"#4a5568" },
                    { l:"PATRON",    v:curIndic?.patterns?.[0]?.name || "—", c:curIndic?.patterns?.[0]?.signal==="BUY"?"#00d4a0":"#4a5568" },
                    { l:"MERCADO",   v:marketSafe?"OK ✓":"RIESGO ⚠", c:marketSafe?"#00d4a0":"#ff4466" },
                  ].map(item => (
                    <div key={item.l} style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)", borderRadius:7, padding:"7px 8px", textAlign:"center" }}>
                      <div style={{ fontSize:7, color:"#6b7a8d", letterSpacing:1, marginBottom:3 }}>{item.l}</div>
                      <div style={{ fontSize:11, fontWeight:700, color:item.c, fontFamily:"monospace" }}>{item.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TRADES */}
            {activeTab==="stats" && (
              <div style={{ padding:"14px 16px", animation:"fadeIn 0.2s" }}>

                {/* Estadísticas avanzadas */}
                <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:10 }}>RENDIMIENTO DEL BOT</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
                  {[
                    { l:"WIN RATE",      v: botStats?.winRate!=null?`${botStats.winRate}%`:"—",    c: (botStats?.winRate||0)>=55?"#00d4a0":"#ff4466" },
                    { l:"SHARPE RATIO",  v: botStats?.sharpe!=null?botStats.sharpe.toFixed(2):"—", c: (botStats?.sharpe||0)>=1?"#00d4a0":(botStats?.sharpe||0)>=0?"#f5a623":"#ff4466" },
                    { l:"SORTINO",       v: botStats?.sortino!=null?botStats.sortino.toFixed(2):"—", c: (botStats?.sortino||0)>=1?"#00d4a0":"#f5a623" },
                    { l:"PROFIT FACTOR", v: botStats?.profitFactor!=null?botStats.profitFactor.toFixed(2):"—", c: (botStats?.profitFactor||0)>=1.5?"#00d4a0":"#ff4466" },
                    { l:"MAX DRAWDOWN",  v: botStats?.maxDrawdown!=null?`${botStats.maxDrawdown.toFixed(1)}%`:"—", c: (botStats?.maxDrawdown||0)<10?"#00d4a0":"#ff4466" },
                    { l:"TOTAL TRADES",  v: botStats?.totalTrades??0,  c:"#8892a4" },
                    { l:"AVG WIN",       v: botStats?.avgWin!=null?`${botStats.avgWin.toFixed(2)}%`:"—", c:"#00d4a0" },
                    { l:"AVG LOSS",      v: botStats?.avgLoss!=null?`${botStats.avgLoss.toFixed(2)}%`:"—", c:"#ff4466" },
                  ].map(s => (
                    <div key={s.l} style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
                      <div style={{ fontSize:7, color:"#6b7a8d", letterSpacing:1.5, marginBottom:4 }}>{s.l}</div>
                      <div style={{ fontSize:16, fontWeight:700, color:s.c, fontFamily:"monospace" }}>{s.v}</div>
                    </div>
                  ))}
                </div>

                {/* Fear & Greed */}
                {/* Fear & Greed mini */}
            {fearGreed && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:8 }}>FEAR & GREED INDEX</div>
                    <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8, padding:"12px 14px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                        <span style={{ fontSize:24 }}>{fearGreed?.value<=20?"😱":fearGreed?.value<=35?"😨":fearGreed?.value<=50?"😐":fearGreed?.value<=65?"😏":"🤑"}</span>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontSize:28, fontWeight:700, fontFamily:"monospace",
                            color:fearGreed?.value<=35?"#00d4a0":fearGreed?.value>=65?"#ff4466":"#f5a623" }}>
                            {fearGreed?.value}
                          </div>
                          <div style={{ fontSize:9, color:"#8892a4" }}>{fearGreed?.label}</div>
                        </div>
                      </div>
                      <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:4, height:6, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${fearGreed?.value}%`, borderRadius:4,
                          background:fearGreed?.value<=35?"#00d4a0":fearGreed?.value>=65?"#ff4466":"#f5a623",
                          transition:"width 0.5s" }}/>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                        <span style={{ fontSize:7, color:"#00d4a0" }}>MIEDO EXTREMO</span>
                        <span style={{ fontSize:7, color:"#ff4466" }}>CODICIA EXTREMA</span>
                      </div>
                      <div style={{ fontSize:8, color:"#4a5568", marginTop:6 }}>
                        {fearGreed?.value<=20?"💎 Oportunidad histórica de compra":
                         fearGreed?.value<=35?"📈 Zona de acumulación favorable":
                         fearGreed?.value>=80?"⚠️ Corrección probable — reducir exposición":
                         fearGreed?.value>=65?"😏 Mercado optimista — cautela":
                         "😐 Sentimiento neutral"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Funding Rates */}
                {Object.keys(fundingRates).length > 0 && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:8 }}>FUNDING RATES</div>
                    <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8, padding:"10px 12px" }}>
                      {coinList.filter(c => fundingRates[c.id]).map(c => {
                        const fr = fundingRates[c.id];
                        return (
                          <div key={c.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"4px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                            <span style={{ fontSize:10, fontWeight:700, color:"#e8eaf0" }}>{c.symbol}</span>
                            <span style={{ fontSize:9, fontFamily:"monospace",
                              color: fr.bullish?"#00d4a0":fr.bearish?"#ff4466":"#8892a4" }}>
                              {fr.ratePct > 0 ? "+" : ""}{fr.ratePct}%
                              {fr.bullish?" 📈":fr.bearish?" 📉":""}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Curva de Equity */}
                {equityData.length > 2 && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:8 }}>CURVA DE EQUITY</div>
                    <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8, padding:"10px 12px" }}>
                      <svg width="100%" height="80" viewBox={`0 0 300 80`} preserveAspectRatio="none">
                        {(() => {
                          const vals  = equityData.map(e => e.v);
                          const minV  = Math.min(...vals);
                          const maxV  = Math.max(...vals);
                          const range = maxV - minV || 1;
                          if (vals.length < 2) return null;
                          const pts   = vals.map((v,i) => `${(i/(vals.length-1)*300).toFixed(1)},${(80-(v-minV)/range*75).toFixed(1)}`).join(" ");
                          const last  = vals[vals.length-1];
                          const first = vals[0];
                          const color = last >= first ? "#00d4a0" : "#ff4466";
                          return (
                            <>
                              <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5"/>
                              <text x="2" y="10" fill="#6b7a8d" fontSize="8">${maxV.toFixed(2)}</text>
                              <text x="2" y="78" fill="#6b7a8d" fontSize="8">${minV.toFixed(2)}</text>
                            </>
                          );
                        })()}
                      </svg>
                      <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                        <span style={{ fontSize:8, color:"#4a5568" }}>Inicio: ${equityData[0]?.v?.toFixed(2)}</span>
                        <span style={{ fontSize:8, color:(equityData[equityData.length-1]?.v||0)>=(equityData[0]?.v||0)?"#00d4a0":"#ff4466" }}>
                          Actual: ${equityData[equityData.length-1]?.v?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rendimiento por hora */}
                {hourlyPerf?.best && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:8 }}>RENDIMIENTO POR HORA</div>
                    <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8, padding:"10px 12px" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                        <div style={{ padding:"6px 8px", borderRadius:6, background:"rgba(0,212,160,0.06)", border:"1px solid rgba(0,212,160,0.15)" }}>
                          <div style={{ fontSize:7, color:"#6b7a8d", marginBottom:2 }}>MEJOR HORA</div>
                          <div style={{ fontSize:13, fontWeight:700, color:"#00d4a0", fontFamily:"monospace" }}>{String(hourlyPerf.best.hour).padStart(2,"0")}:00</div>
                          <div style={{ fontSize:8, color:"#00d4a0" }}>+{hourlyPerf.best.avgPnl?.toFixed(2)}% avg</div>
                        </div>
                        <div style={{ padding:"6px 8px", borderRadius:6, background:"rgba(255,68,102,0.06)", border:"1px solid rgba(255,68,102,0.15)" }}>
                          <div style={{ fontSize:7, color:"#6b7a8d", marginBottom:2 }}>PEOR HORA</div>
                          <div style={{ fontSize:13, fontWeight:700, color:"#ff4466", fontFamily:"monospace" }}>{String(hourlyPerf.worst.hour).padStart(2,"0")}:00</div>
                          <div style={{ fontSize:8, color:"#ff4466" }}>{hourlyPerf.worst.avgPnl?.toFixed(2)}% avg</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Paper Trading Stats */}
                {paperStats && (paperStats.wins + paperStats.losses) > 0 && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:8 }}>PAPER TRADING (PARALELO)</div>
                    <div style={{ background:"rgba(77,166,255,0.05)", border:"1px solid rgba(77,166,255,0.15)", borderRadius:8, padding:"10px 12px" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                        {[
                          { l:"WINS",  v: paperStats.wins,   c:"#00d4a0" },
                          { l:"LOSSES",v: paperStats.losses, c:"#ff4466" },
                          { l:"PnL",   v: `${paperStats.pnl?.toFixed(2)}%`, c: paperStats.pnl>=0?"#00d4a0":"#ff4466" },
                        ].map(s => (
                          <div key={s.l} style={{ textAlign:"center" }}>
                            <div style={{ fontSize:7, color:"#4a5568", letterSpacing:1 }}>{s.l}</div>
                            <div style={{ fontSize:14, fontWeight:700, color:s.c, fontFamily:"monospace" }}>{s.v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Backtesting */}
                <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:8 }}>BACKTESTING RÁPIDO</div>
                <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8, padding:"10px 12px" }}>
                  <div style={{ fontSize:8, color:"#8892a4", marginBottom:8 }}>Prueba la estrategia actual contra datos históricos reales (últimas 200h)</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {COINS.slice(0,6).map(c => (
                      <button key={c.id} onClick={async () => {
                        addLog(`Ejecutando backtest ${c.symbol}...`, "info");
                        const r = await apiGet(`/api/backtest/${c.symbol}`);
                        if (r.ok) {
                          addLog(`Backtest ${c.symbol}: ${r.trades} trades | WR:${r.winRate} | PnL:${r.totalPnl}`, r.totalPnl?.includes("-")?"warn":"ok");
                          alert(`📊 Backtest ${c.symbol} (${r.period})

Trades: ${r.trades} (${r.wins}W/${r.losses}L)
Win Rate: ${r.winRate}
PnL total: ${r.totalPnl}
Capital final: ${r.capitalFinal}`);
                        } else {
                          addLog(`Error backtest ${c.symbol}: ${r.error}`, "error");
                        }
                      }} style={{ padding:"5px 10px", borderRadius:5, border:"1px solid rgba(0,212,160,0.2)", background:"rgba(0,212,160,0.05)", color:"#00d4a0", fontFamily:"monospace", fontSize:8, cursor:"pointer" }}>
                        {c.symbol}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab==="trades" && (
              <div style={{ padding:"12px 14px", animation:"fadeIn 0.2s" }}>
                {trades.filter(t => t.pnl!=null).length > 2 && (() => {
                  const closed = trades.filter(t => t.pnl!=null && !isNaN(parseFloat(t.pnl)));
                  let eq = 100; const pts = [eq];
                  closed.forEach(t => { eq *= (1+parseFloat(t.pnl||0)/100); pts.push(eq); });
                  const min=Math.min(...pts), max=Math.max(...pts), rng=max-min||1;
                  const last=pts[pts.length-1], clr=last>=100?"#00d4a0":"#ff4466";
                  const W=400, H=40;
                  const svgPts=pts.map((v,i)=>`${(i/(pts.length-1))*W},${H-4-((v-min)/rng)*(H-8)}`).join(" ");
                  return (
                    <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)", borderRadius:8, padding:"8px 10px", marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                        <span style={{ fontSize:8, color:"#4a5568", letterSpacing:1 }}>EQUITY ACUMULADO</span>
                        <span style={{ fontSize:11, fontWeight:700, color:clr, fontFamily:"monospace" }}>{last>=100?"+":""}{(last-100).toFixed(2)}%</span>
                      </div>
                      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:"block" }}>
                        <polygon points={`0,${H} ${svgPts} ${W},${H}`} fill={`${clr}18`}/>
                        <polyline points={svgPts} fill="none" stroke={clr} strokeWidth="1.5" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  );
                })()}
                <div className="portfolio-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6, marginBottom:10 }}>
                  {[
                    { l:"TRADES",   v:trades.length, c:"#8892a4" },
                    { l:"WIN RATE", v:winRateNet!=null?`${winRateNet}%`:"—", c:winRateNet!=null?(winRateNet>=50?"#00d4a0":"#ff4466"):"#8892a4" },
                    { l:"P&L NETO", v:totalNetPct?`${totalNetPct>=0?"+":""}${totalNetPct.toFixed(2)}%`:"—", c:totalNetPct>=0?"#00d4a0":"#ff4466" },
                    { l:"FEES",     v:`$${totalFeesUSD.toFixed(2)}`, c:"#f5a623" },
                  ].map(s => (
                    <div key={s.l} style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)", borderRadius:7, padding:"8px", textAlign:"center" }}>
                      <div style={{ fontSize:7, color:"#4a5568", letterSpacing:1, marginBottom:3 }}>{s.l}</div>
                      <div style={{ fontSize:13, fontWeight:700, color:s.c, fontFamily:"monospace" }}>{s.v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ maxHeight:350, overflowY:"auto" }}>
                  {trades.length===0 ? (
                    <div style={{ textAlign:"center", color:"#4a5568", padding:30, fontSize:10 }}>Sin operaciones registradas</div>
                  ) : [...trades].reverse().map((t,i) => {
                    const c=COINS.find(x=>x.symbol===t.symbol);
                    const curP=c?prices[c.id]:0;
                    const livePnl=t.action==="BUY"&&t.pnl==null&&curP>0&&t.price>0?((curP-t.price)/t.price*100):null;
                    return (
                      <div key={i} style={{ padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:2 }}>
                          <div style={{ display:"flex", gap:7, alignItems:"center" }}>
                            <span style={{ fontSize:8, padding:"2px 6px", borderRadius:4, fontWeight:700, background:t.action==="BUY"?"rgba(0,212,160,0.12)":"rgba(255,68,102,0.12)", color:t.action==="BUY"?"#00d4a0":"#ff4466" }}>{t.action}</span>
                            <span style={{ fontSize:11, fontWeight:700, color:"#e8eaf0" }}>{t.symbol}</span>
                          </div>
                          {t.pnl!=null ? (
                            <span style={{ fontSize:11, fontWeight:700, color:parseFloat(t.pnl)>=0?"#00d4a0":"#ff4466", fontFamily:"monospace" }}>{parseFloat(t.pnl)>=0?"+":""}{parseFloat(t.pnl).toFixed(2)}%</span>
                          ) : livePnl!=null ? (
                            <span style={{ fontSize:10, color:livePnl>=0?"#00d4a0":"#ff4466", fontFamily:"monospace" }}>{livePnl>=0?"+":""}{livePnl.toFixed(2)}% <span style={{ fontSize:8, color:"#4a5568" }}>live</span></span>
                          ) : <span style={{ fontSize:9, color:"#4a5568" }}>abierto</span>}
                        </div>
                        <div style={{ display:"flex", gap:10, fontSize:8, color:"#4a5568" }}>
                          <span>{fmt$(t.price)}</span>
                          <span>Qty {(() => { const _c=COINS.find(x=>x.symbol===t.symbol); const _d=_c?.decimals??4; const _q=parseFloat(t.qty||0); return isNaN(_q)?t.qty:_q.toFixed(Math.min(_d,6)); })()}</span>
                          {t.reason && <span style={{ color:t.reason==="TAKE-PROFIT"?"#00d4a0":t.reason==="STOP-LOSS"?"#ff4466":"#4a5568" }}>{t.reason}</span>}
                          <span style={{ marginLeft:"auto" }}>{t.date} {t.time}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CONFIG */}
            {activeTab==="config" && (
              <div style={{ padding:"14px 16px", animation:"fadeIn 0.2s" }}>

                {/* ── MODO PAPER/REAL ── */}
                <button onClick={async()=>{ const n=!paperMode; if(!n&&!window.confirm("⚠️ Activar modo REAL. El bot operará con dinero real. ¿Confirmas?"))return; setPaperMode(n); await apiPost("/api/config",{paperMode:n}); }}
                  style={{ width:"100%", padding:"12px", borderRadius:8, border:`1px solid ${paperMode?"rgba(255,255,255,0.08)":"rgba(255,68,102,0.35)"}`, background:paperMode?"rgba(255,255,255,0.02)":"rgba(255,68,102,0.08)", color:paperMode?"#8892a4":"#ff4466", fontFamily:"monospace", fontSize:11, cursor:"pointer", marginBottom:16, letterSpacing:1 }}>
                  {paperMode?"📄 MODO PAPER (simulado) — pulsa para activar REAL":"⚡ MODO REAL ACTIVO — pulsa para volver a PAPER"}
                </button>

                {/* ── PRESETS RÁPIDOS ── */}
                <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:10 }}>ESTRATEGIA</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:20 }}>
                  {[
                    { l:"CONSERVADOR", desc:"Solo señales muy claras", c:82, s:20, sl:2, tp:4, int:60, max:4, col:"#00d4a0",
                      info:"Win rate alto, pocas operaciones. Ideal para empezar." },
                    { l:"MODERADO",    desc:"Balance riesgo/beneficio", c:75, s:30, sl:3, tp:6, int:60, max:6, col:"#f5a623",
                      info:"Configuración actual recomendada." },
                    { l:"AGRESIVO",   desc:"Más operaciones, más riesgo", c:68, s:40, sl:4, tp:8, int:45, max:8, col:"#ff4466",
                      info:"Mayor exposición, mayor potencial." },
                  ].map(p => (
                    <button key={p.l} onClick={()=>{
                      setMinConf(p.c); setOrderSize(p.s); setStopLossPct(p.sl);
                      setTakeProfitPct(p.tp); setIntervalMin(p.int); setMaxPositions(p.max);
                      apiPost("/api/config",{minConfidence:p.c,orderSize:p.s,stopLossPct:p.sl,takeProfitPct:p.tp,intervalMinutes:p.int,maxOpenPositions:p.max});
                    }} style={{ padding:"12px 8px", borderRadius:8, border:`1px solid ${p.col}44`, background:`${p.col}08`, color:p.col, fontFamily:"monospace", fontSize:9, cursor:"pointer", textAlign:"center", lineHeight:1.8 }}>
                      <div style={{ fontWeight:700, fontSize:10 }}>{p.l}</div>
                      <div style={{ opacity:.6, fontSize:8, marginTop:2 }}>{p.desc}</div>
                      <div style={{ opacity:.4, fontSize:7, marginTop:4, borderTop:`1px solid ${p.col}22`, paddingTop:4 }}>
                        Conf {p.c}% · ${p.s}/op · SL {p.sl}%
                      </div>
                    </button>
                  ))}
                </div>

                {/* ── PARÁMETROS CLAVE ── */}
                <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:10 }}>PARÁMETROS</div>
                <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, padding:"12px 14px", marginBottom:20 }}>
                  {[
                    { l:"Tamaño por operación", val:orderSize, set:setOrderSize, min:1, max:500, step:1, unit:"$", col:"#00d4a0", key:"orderSize",
                      hint:"Cuánto dinero arriesgas por cada compra" },
                    { l:"Confianza mínima IA", val:minConf, set:setMinConf, min:50, max:95, step:5, unit:"%", col:minConf>=80?"#00d4a0":minConf>=65?"#f5a623":"#ff4466", key:"minConfidence",
                      hint:"% mínimo de confianza para que el bot compre" },
                    { l:"Máx posiciones abiertas", val:maxPositions, set:setMaxPositions, min:1, max:10, step:1, unit:"", col:"#4da6ff", key:"maxOpenPositions",
                      hint:"Número máximo de compras simultáneas" },
                    { l:"Intervalo de análisis", val:intervalMin, set:setIntervalMin, min:15, max:240, step:15, unit:"min", col:"#8892a4", key:"intervalMinutes",
                      hint:"Cada cuánto analiza el mercado" },
                    { l:"Drawdown diario máximo", val:maxDailyDrawdown, set:setMaxDailyDrawdown, min:3, max:20, step:1, unit:"%", col:maxDailyDrawdown<=5?"#ff4466":maxDailyDrawdown<=10?"#f5a623":"#8892a4", key:"maxDailyDrawdown",
                      hint:"Bot se pausa si las pérdidas del día superan este %" },
                  ].map(s => (
                    <div key={s.l} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                        <div>
                          <span style={{ fontSize:10, color:"#e8eaf0" }}>{s.l}</span>
                          <div style={{ fontSize:8, color:"#7a8899", marginTop:1 }}>{s.hint}</div>
                        </div>
                        <span style={{ fontSize:14, fontWeight:700, color:s.col, fontFamily:"monospace" }}>{s.val}{s.unit}</span>
                      </div>
                      <input type="range" min={s.min} max={s.max} step={s.step} value={s.val}
                        onChange={e=>s.set(Number(e.target.value))}
                        onMouseUp={e=>apiPost("/api/config",{[s.key]:Number(e.target.value)})}
                        onTouchEnd={e=>apiPost("/api/config",{[s.key]:Number(e.target.value)})}
                        style={{ width:"100%", accentColor:s.col }}/>
                    </div>
                  ))}

                  {/* SL/TP info */}
                  <div style={{ marginTop:8, padding:"8px 10px", background:"rgba(77,166,255,0.05)", border:"1px solid rgba(77,166,255,0.15)", borderRadius:7 }}>
                    <div style={{ fontSize:8, color:"#4da6ff", letterSpacing:1, marginBottom:4 }}>ℹ️ SL/TP/KELLY AUTOMÁTICOS</div>
                    <div style={{ fontSize:9, color:"#8892a4", lineHeight:1.8 }}>
                      • <span style={{ color:"#4da6ff" }}>Stop-Loss</span>: ATR×(1.5-3) según ADX. Ajustado a Fibonacci y S/R.<br/>
                      • <span style={{ color:"#4da6ff" }}>Take-Profit</span>: TP1→TP2→TP3 escalado. Ajustado a niveles psicológicos.<br/>
                      • <span style={{ color:"#4da6ff" }}>Tamaño real</span>: $orden × Kelly × riskMult × ML moneda.
                      {curIndic?.atr && <><br/>• ATR {coin.symbol}: {curIndic.atr.atrPct}% | SL≈{curIndic.atr.dynamicSL?.toFixed(4)}</>}
                    </div>
                  </div>
                </div>

                {/* ── MONEDAS ACTIVAS ── */}
                <div style={{ fontSize:9, color:"#7a8899", letterSpacing:2, marginBottom:10 }}>MONEDAS ACTIVAS</div>
                <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, padding:"10px 12px", marginBottom:12 }}>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                    {coinList.map(c => (
                      <div key={c.id} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:20, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)" }}>
                        <span style={{ fontSize:10, fontWeight:700, color:"#e8eaf0" }}>{c.symbol}</span>
                        <span style={{ fontSize:8, color: (changes[c.id]||0)>=0?"#00d4a0":"#ff4466", fontFamily:"monospace" }}>
                          {(changes[c.id]||0)>=0?"+":""}{(changes[c.id]||0).toFixed(1)}%
                        </span>
                        {!["BTC","ETH"].includes(c.symbol) && (
                          <button onClick={async()=>{
                            if(!window.confirm(`¿Eliminar ${c.symbol}?`)) return;
                            const r = await apiPost("/api/coins/remove",{symbol:c.symbol});
                            if(r.ok){setCoinList(r.coins);setCoinMsg(`✓ ${c.symbol} eliminada`);}
                            else setCoinMsg(`✗ ${r.error}`);
                            setTimeout(()=>setCoinMsg(null),3000);
                          }} style={{ fontSize:10, background:"none", border:"none", color:"#4a5568", cursor:"pointer", padding:"0 2px", lineHeight:1 }}>×</button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <input value={newCoinSym} onChange={e=>setNewCoinSym(e.target.value.toUpperCase())}
                      placeholder="Símbolo (ej: XRP, DOGE...)"
                      style={{ flex:1, padding:"8px 10px", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:6, color:"#e8eaf0", fontFamily:"monospace", fontSize:10, outline:"none" }}
                      onKeyDown={e=>{ if(e.key==="Enter"&&newCoinSym) document.getElementById("btn-add-coin").click(); }}
                    />
                    <button id="btn-add-coin" disabled={!newCoinSym||addingCoin} onClick={async()=>{
                      setAddingCoin(true);
                      const r = await apiPost("/api/coins/add",{symbol:newCoinSym,pair:newCoinSym+"USDT",decimals:2});
                      if(r.ok){
                        setCoinList(r.coins);
                        setNewCoinSym("");
                        const newC = r.coins.find(c => c.symbol === newCoinSym);
                        if(newC) {
                          setCoinMsg(`⟳ Cargando ${newC.symbol}...`);
                          fetchRealHistory(newC).then(() => setCoinMsg(`✓ ${newC.symbol} añadida — indicadores listos`));
                          fetch(`${CONFIG.PROXY}/api/bitunix/ticker?symbol=${newC.pair}`).then(x=>x.json()).then(d=>{
                            if(d.code===0&&d.data){setPrices(p=>({...p,[newC.id]:parseFloat(d.data.lastPrice||0)}));setChanges(p=>({...p,[newC.id]:parseFloat(d.data.priceChange24h||0)}));}
                          }).catch(()=>{});
                          setCoin(newC);
                        } else setCoinMsg(`✓ ${newCoinSym} añadida`);
                      }
                      else setCoinMsg(`✗ ${r.error}`);
                      setAddingCoin(false);
                      setTimeout(()=>setCoinMsg(null),4000);
                    }} style={{ padding:"8px 14px", borderRadius:6, border:"1px solid rgba(0,212,160,0.3)", background:"rgba(0,212,160,0.08)", color:"#00d4a0", fontFamily:"monospace", fontSize:9, cursor:"pointer", opacity:!newCoinSym||addingCoin?.5:1 }}>
                      {addingCoin?"...":"+ AÑADIR"}
                    </button>
                    <button onClick={async()=>{ if(!window.confirm("¿Restaurar monedas originales?"))return; const r=await apiPost("/api/coins/reset"); if(r.ok)setCoinList(r.coins); }}
                      style={{ padding:"8px 10px", borderRadius:6, border:"1px solid rgba(255,255,255,0.07)", background:"transparent", color:"#4a5568", fontFamily:"monospace", fontSize:9, cursor:"pointer" }}>
                      ↺
                    </button>
                  </div>
                  {coinMsg && <div style={{ fontSize:9, padding:"5px 8px", borderRadius:5, marginTop:8, background:coinMsg.includes("✓")?"rgba(0,212,160,0.08)":"rgba(255,68,102,0.08)", color:coinMsg.includes("✓")?"#00d4a0":"#ff4466", fontFamily:"monospace" }}>{coinMsg}</div>}
                </div>

              </div>
            )}

            {activeTab==="logs" && (
              <div style={{ padding:"12px 14px", animation:"fadeIn 0.2s" }}>
                {(()=>{
                  const today    = new Date().toLocaleDateString("es-ES",{timeZone:"Europe/Madrid"});
                  const todayLogs= logs.filter(l=>!l.date||l.date===today);
                  const oldLogs  = logs.filter(l=>l.date&&l.date!==today);
                  const base     = showAllLogs ? logs : todayLogs;
                  const visible  = logFilter==="all" ? base : base.filter(l=>(l.type||"info")===logFilter);
                  return (
                    <>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, gap:6 }}>
                        <div style={{ display:"flex", gap:4 }}>
                          {[["all","TODO","#7a8899"],["ok","✓","#00d4a0"],["warn","⚠","#f5a623"],["error","✕","#ff4466"]].map(([f,lbl,clr]) => (
                            <button key={f} onClick={()=>setLogFilter(f)}
                              style={{ fontSize:7, padding:"2px 7px", borderRadius:3, cursor:"pointer", fontFamily:"monospace",
                                background: logFilter===f?`${clr}18`:"rgba(255,255,255,0.02)",
                                border: `1px solid ${logFilter===f?clr:"rgba(255,255,255,0.07)"}`,
                                color: logFilter===f?clr:"#6b7a8d" }}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                          <span style={{ fontSize:8, color:"#6b7a8d" }}>{visible.length}/{todayLogs.length} logs</span>
                          {oldLogs.length>0 && <button onClick={()=>setShowAllLogs(v=>!v)} style={{ fontSize:8, padding:"2px 8px", background:"transparent", border:"1px solid rgba(255,255,255,0.07)", color:"#6b7a8d", borderRadius:4, cursor:"pointer", fontFamily:"monospace" }}>{showAllLogs?"Hoy":"Todo"}</button>}
                          <button onClick={()=>{ setLogs([]); try{localStorage.removeItem(LOGS_KEY);}catch(_){} }}
                            style={{ fontSize:7, padding:"2px 7px", borderRadius:3, background:"rgba(255,68,102,0.06)", border:"1px solid rgba(255,68,102,0.15)", color:"#ff4466", cursor:"pointer", fontFamily:"monospace" }}>
                            🗑
                          </button>
                        </div>
                      </div>
                      <div style={{ background:"#07090d", border:"1px solid rgba(255,255,255,0.05)", borderRadius:8, padding:"8px 10px", maxHeight:420, overflowY:"auto", fontFamily:"monospace", fontSize:9 }}>
                        {visible.length===0
                          ? <div style={{ color:"#4a5568", textAlign:"center", padding:20 }}>
                              {logFilter==="all"?"Sin logs de hoy":`Sin logs de tipo "${logFilter}" hoy`}
                            </div>
                          : visible.map((l,i) => {
                            const isToday=!l.date||l.date===today;
                            const clr=l.type==="ok"?"#00d4a0":l.type==="error"?"#ff4466":l.type==="warn"?"#f5a623":"#8892a4";
                            return (
                              <div key={i} style={{ display:"flex", gap:6, padding:"4px 0", borderBottom:"1px solid rgba(255,255,255,0.02)", opacity:isToday?1:.35 }}>
                                <span style={{ color:"#5a6478", minWidth:isToday?52:96, flexShrink:0, fontSize:8 }}>{isToday?l.time:`${l.date} ${l.time}`}</span>
                                <span style={{ fontSize:7, padding:"1px 4px", borderRadius:2, flexShrink:0, alignSelf:"flex-start", marginTop:1,
                                  background: l.type==="ok"?"rgba(0,212,160,0.1)":l.type==="error"?"rgba(255,68,102,0.1)":l.type==="warn"?"rgba(245,166,35,0.1)":"rgba(255,255,255,0.04)",
                                  color: clr }}>{l.type||"info"}</span>
                                <span style={{ color:clr, lineHeight:1.5, fontSize:9, flex:1 }}>{l.msg}</span>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

          </div>
        </div>

        {/* SIDEBAR */}
        <div className="sidebar-hide" style={{ background:"#0d1117", borderLeft:"1px solid rgba(255,255,255,0.05)", display:"flex", flexDirection:"column", overflowY:"auto", minWidth:0 }}>

          {/* PORTFOLIO MINI */}
          <div style={{ padding:"10px 14px", borderBottom:"1px solid rgba(255,255,255,0.05)", background:"rgba(0,212,160,0.02)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
              <span style={{ fontSize:8, color:"#7a8899", letterSpacing:2 }}>PORTFOLIO</span>
              <span style={{ fontSize:16, fontWeight:700, color:"#e8eaf0", fontFamily:"monospace" }}>
                ${totalBalanceUSD > 0 ? totalBalanceUSD.toFixed(2) : (usdtBal+usdcBal).toFixed(2)}
              </span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:4 }}>
              {[
                { l:"USDT libre", v:`$${usdtBal.toFixed(2)}`,  c:"#8892a4" },
                { l:"En posic.",  v:`$${Object.entries(stopLosses).reduce((s,[id,o])=>{
                  const p = prices[id] || 0;
                  const q = parseFloat(o?.qty||0);
                  return s + (isNaN(q)||!p ? 0 : q*p);
                },0).toFixed(2)}`, c:"#4da6ff" },
                { l:"WR",         v:winRateNet!=null?`${winRateNet}%`:"—", c:winRateNet>=55?"#00d4a0":winRateNet>=45?"#f5a623":"#ff4466" },
              ].map(s => (
                <div key={s.l} style={{ textAlign:"center", padding:"5px 4px", background:"rgba(255,255,255,0.02)", borderRadius:6 }}>
                  <div style={{ fontSize:7, color:"#4a5568", marginBottom:2 }}>{s.l}</div>
                  <div style={{ fontSize:10, fontWeight:700, color:s.c, fontFamily:"monospace" }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* BOT STATUS */}
          <div style={{ padding:"12px 14px", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 10px", background:autoMode?"rgba(0,212,160,0.06)":"rgba(255,255,255,0.02)", border:`1px solid ${autoMode?"rgba(0,212,160,0.2)":"rgba(255,255,255,0.06)"}`, borderRadius:8, marginBottom:8 }}>
              {autoMode && <div className="live-dot" style={{ width:7, height:7, background:"#00d4a0", borderRadius:"50%", flexShrink:0 }}/>}
              <div style={{ flex:1 }}>
                <div style={{ fontSize:10, fontWeight:700, color:autoMode?"#00d4a0":"#8892a4" }}>
                  {autoMode ? (paperMode?"📄 PAPER ACTIVO":"⚡ BOT REAL ACTIVO") : "⏸ BOT DETENIDO"}
                </div>
                <div style={{ fontSize:8, color:autoMode?"rgba(0,212,160,0.6)":"#7a8899" }}>
                  {autoMode ? `${intervalMin}min · max ${maxPositions} pos · conf ${minConf}%` : "Pulsa START para activar"}
                </div>
              </div>
              {/* Indicador de riesgo */}
              {autoMode && botStats?.winRate != null && (
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:9, fontWeight:700, fontFamily:"monospace", color: botStats.winRate>=55?"#00d4a0":botStats.winRate>=45?"#f5a623":"#ff4466" }}>
                    {botStats.winRate}%
                  </div>
                  <div style={{ fontSize:7, color:"#6b7a8d" }}>WR</div>
                </div>
              )}
            </div>
            <div style={{ display:"flex", gap:6, marginTop:8 }}>
              <button onClick={async()=>{ const n=!autoMode; setAutoMode(n); if(n){await apiPost("/api/start");addLog("Bot iniciado — primer análisis en breve","ok");}else{await apiPost("/api/stop");addLog("Bot detenido","warn");} }}
                className="btn-start" style={{ flex:2, padding:"10px 6px", borderRadius:7,
                  border:`1px solid ${autoMode?"rgba(255,68,102,0.4)":"rgba(0,212,160,0.4)"}`,
                  background:autoMode?"rgba(255,68,102,0.1)":"rgba(0,212,160,0.1)",
                  color:autoMode?"#ff4466":"#00d4a0", fontFamily:"monospace", fontSize:10,
                  cursor:"pointer", fontWeight:700, letterSpacing:.5,
                  boxShadow:autoMode?"0 0 12px rgba(255,68,102,0.1)":"0 0 12px rgba(0,212,160,0.1)"
                }}>
                {autoMode ? "⏹ STOP" : analyzing ? "⟳ ..." : "▶ START"}
              </button>
              <button onClick={() => runAnalysis(coin)} disabled={analyzing||!proxyOk}
                style={{ flex:1, padding:"10px 6px", borderRadius:7, border:"1px solid rgba(77,166,255,0.3)", background:"rgba(77,166,255,0.06)", color:"#4da6ff", fontFamily:"monospace", fontSize:9, cursor:"pointer", opacity:!proxyOk?.5:1 }}
                title={`Analizar ${coin.symbol}`}>
                {analyzing?"⟳":`⚡ ${coin.symbol}`}
              </button>
              <button onClick={async()=>{ await syncFromServer(); addLog("Sync completado","ok"); }}
                style={{ flex:1, padding:"10px 6px", borderRadius:7, border:"1px solid rgba(77,166,255,0.2)", background:"rgba(77,166,255,0.04)", color:"#4da6ff", fontFamily:"monospace", fontSize:9, cursor:"pointer" }}
                title="Sincronizar con Bitunix">🔄</button>
              <button onClick={() => runAllAnalysis()} disabled={analyzingAll||!proxyOk}
                style={{ flex:1, padding:"10px 6px", borderRadius:7, border:"1px solid rgba(77,166,255,0.15)", background:"rgba(77,166,255,0.03)", color: analyzingAll?"#f5a623":"#4da6ff", fontFamily:"monospace", fontSize:9, cursor:(!proxyOk||analyzingAll)?"not-allowed":"pointer", opacity:!proxyOk?.5:1 }}
                title="Analizar todas las monedas">
                {analyzingAll ? `⟳ ${allProgress||"..."}` : "⚡⚡"}
              </button>
            </div>
          </div>

          {/* BTC MARKET */}
          <div style={{ padding:"12px 14px", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <span style={{ fontSize:9, color:"#7a8899", letterSpacing:2 }}>MERCADO GLOBAL</span>
              <span style={{ fontSize:8, padding:"2px 7px", borderRadius:4,
                background: marketSafe?"rgba(0,212,160,0.1)":"rgba(255,68,102,0.1)",
                color: marketSafe?"#00d4a0":"#ff4466" }}>
                {marketSafe?"✓ SEGURO":"⚠ CAUTELOSO"}
              </span>
            </div>
            {/* BTC principal */}
            <div style={{ padding:"8px 10px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.05)", borderRadius:7, marginBottom:6 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:10, fontWeight:700, color:"#f5a623" }}>₿ BTC</div>
                  <div style={{ fontSize:8, color:"#4a5568" }}>
                    1H: <span style={{ color:btcTrend?.change1h>=0?"#00d4a0":"#ff4466" }}>{btcTrend?`${btcTrend?.change1h>=0?"+":""}${btcTrend?.change1h?.toFixed(2)}%`:"—"}</span>
                    {" · "}4H: <span style={{ color:btcTrend?.change4h>=0?"#00d4a0":"#ff4466" }}>{btcTrend?`${btcTrend?.change4h>=0?"+":""}${btcTrend?.change4h?.toFixed(2)}%`:"—"}</span>
                  </div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:12, fontWeight:700, fontFamily:"monospace", color:"#e8eaf0" }}>${prices["bitcoin"]?.toFixed(0)||"—"}</div>
                  <div style={{ fontSize:8, color: btcTrend?.rsi>70?"#ff4466":btcTrend?.rsi<30?"#00d4a0":"#8892a4" }}>RSI {btcTrend?.rsi?.toFixed(0)||"—"}</div>
                </div>
              </div>
            </div>
            {/* Régimen global de mercado (BTC como referencia) */}
            {globalRegime && (() => {
              const _r   = globalRegime.regime;
              const _col = _r==="BULL_TREND"?"#00d4a0":_r==="BEAR_TREND"?"#ff4466":_r==="OVERSOLD"?"#00d4a0":_r==="OVERBOUGHT"?"#ff4466":"#f5a623";
              const _label = {BULL_TREND:"📈 ALCISTA",BEAR_TREND:"📉 BAJISTA",RANGING:"↔ LATERAL",HIGH_VOLATILITY:"⚡ VOLÁTIL",OVERSOLD:"🟢 SOBREVENTA",OVERBOUGHT:"🔴 SOBRECOMPRA"}[_r]||_r;
              return (
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"5px 8px",background:`${_col}11`,border:`1px solid ${_col}33`,borderRadius:6,marginBottom:6}}>
                  <div>
                    <div style={{fontSize:7,color:"#4a5568",letterSpacing:1}}>RÉGIMEN GLOBAL</div>
                    <div style={{fontSize:7,color:"#4a5568",marginTop:1,maxWidth:120}}>{globalRegime.note}</div>
                  </div>
                  <span style={{fontSize:9,fontWeight:700,color:_col}}>{_label}</span>
                </div>
              );
            })()}
            {/* Fear & Greed mini */}
            {fearGreed && (
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)", borderRadius:6, marginBottom:6 }}>
                <span style={{ fontSize:16 }}>{fearGreed?.value<=20?"😱":fearGreed?.value<=35?"😨":fearGreed?.value>=80?"🤑":fearGreed?.value>=65?"😏":"😐"}</span>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontSize:8, color:"#4a5568" }}>Fear & Greed</span>
                    <span style={{ fontSize:9, fontWeight:700, fontFamily:"monospace", color:fearGreed?.value<=35?"#00d4a0":fearGreed?.value>=65?"#ff4466":"#f5a623" }}>{fearGreed?.value}</span>
                  </div>
                  <div style={{ height:3, background:"rgba(255,255,255,0.05)", borderRadius:2, marginTop:3, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${fearGreed?.value}%`, borderRadius:2, background:fearGreed?.value<=35?"#00d4a0":fearGreed?.value>=65?"#ff4466":"#f5a623" }}/>
                  </div>
                </div>
              </div>
            )}
            {/* Sesión actual */}
            {(() => {
              const h = new Date().getUTCHours();
              const sessions = [
                { name:"🌏 ASIA",   range:[0,8],   hot:false },
                { name:"🇪🇺 EUROPA", range:[8,13],  hot:true },
                { name:"🔥 EU/US",   range:[13,17], hot:true },
                { name:"🇺🇸 US",     range:[17,22], hot:true },
                { name:"🌙 CIERRE",  range:[22,24], hot:false },
              ];
              const cur = sessions.find(s => h >= s.range[0] && h < s.range[1]) || sessions[0];
              return (
                <div style={{ fontSize:8, color:cur.hot?"#f5a623":"#4a5568", textAlign:"center", padding:"4px 0" }}>
                  {cur.name} {cur.hot?"· Alta liquidez":"· Baja liquidez"}
                </div>
              );
            })()}
          </div>

          {/* POSITIONS */}
          <div style={{ padding:"12px 14px", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
              <span style={{ fontSize:8, color:"#7a8899", letterSpacing:2 }}>POSICIONES</span>
              <span style={{ fontSize:8, color:Object.keys(stopLosses).length>=maxPositions?"#ff4466":"#00d4a0", fontFamily:"monospace" }}>{Object.keys(stopLosses).length}/{maxPositions}</span>
            </div>
            {Object.keys(stopLosses).length===0 ? (
              <div style={{ textAlign:"center", color:"#4a5568", fontSize:9, padding:"14px 0" }}>Sin posiciones abiertas</div>
            ) : coinList.filter(c=>stopLosses[c.id]).map(c => {
              const sl=stopLosses[c.id], tp=takeProfits[c.id];
              const curP=prices[c.id]||0, ep=sl?.entryPrice&&sl.entryPrice>0?sl.entryPrice:0;
              const pnl=ep>0&&curP>0?((curP-ep)/ep*100):null;
              const val=parseFloat(sl?.qty||0)*curP;
              const usdtInv=ep>0?(parseFloat(sl?.qty||0)*ep).toFixed(2):"?";
              const usdtRec=(parseFloat(sl?.qty||0)*curP*0.9998).toFixed(2); // estimado con 0.02% margen
              return (
                <div key={c.id} style={{ background:"rgba(255,255,255,0.02)", border:`1px solid ${pnl>=0?"rgba(0,212,160,0.15)":"rgba(255,68,102,0.12)"}`, borderRadius:8, padding:"10px", marginBottom:8 }}>
                  {/* Header con símbolo y PnL */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:13, fontWeight:700, color:"#e8eaf0", fontFamily:"'Inter',sans-serif" }}>{c.symbol}</span>
                      {sl?.averaged && <span style={{ fontSize:7, padding:"1px 4px", borderRadius:3, background:"rgba(77,166,255,0.1)", color:"#4da6ff" }}>AVG</span>}
                      {sl?.partialSold && <span style={{ fontSize:7, padding:"1px 4px", borderRadius:3, background:"rgba(245,166,35,0.1)", color:"#f5a623" }}>TP1✓</span>}
                      {sl?.manualEntry && (
                        <span style={{ display:"flex", alignItems:"center", gap:3 }}>
                          <span style={{ fontSize:7, padding:"1px 4px", borderRadius:3, background:"rgba(245,166,35,0.15)", color:"#f5a623", border:"1px solid rgba(245,166,35,0.3)" }}>MANUAL</span>
                          <button onClick={async () => {
                            if (!window.confirm(`¿Eliminar posición manual de ${c.symbol} del registro? (no vende en Bitunix)`)) return;
                            const r = await apiPost("/api/orders/clear-one", { coinId: c.id });
                            if (r.ok) {
                              setStopLosses(prev => { const n={...prev}; delete n[c.id]; return n; });
                              setTakeProfits(prev => { const n={...prev}; delete n[c.id]; return n; });
                              addLog(`${c.symbol}: posición manual eliminada del registro`, "warn");
                            }
                          }} style={{ fontSize:8, padding:"1px 5px", borderRadius:3, cursor:"pointer", background:"rgba(255,68,102,0.1)", border:"1px solid rgba(255,68,102,0.3)", color:"#ff4466" }}>
                            ✕
                          </button>
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize:14, fontWeight:700, color:pnl==null?"#4a5568":pnl>=0?"#00d4a0":"#ff4466", fontFamily:"monospace" }}>{pnl==null?"—%":((pnl>=0?"+":"")+pnl.toFixed(2)+"%")}</span>
                  </div>
                  {/* Barra de PnL visual */}
                  {/* Barra progreso SL → entrada → TP */}
                  {sl?.entryPrice > 0 && sl?.sl > 0 && sl?.tp > 0 && curP > 0 && (
                    <div style={{ marginBottom:6 }}>
                      {(() => {
                        const entry = sl.entryPrice;
                        const slP   = sl.sl;
                        const tpP   = sl.tp;
                        const range = tpP - slP;
                        if (range <= 0) return null;
                        const pos   = Math.max(0, Math.min(100, (curP - slP) / range * 100));
                        const clr   = curP >= entry ? "#00d4a0" : "#ff4466";
                        return (
                          <div>
                            <div style={{ display:"flex", justifyContent:"space-between", fontSize:7, color:"#4a5568", marginBottom:2 }}>
                              <span style={{ color:"#ff4466" }}>SL {fmt$(slP)}</span>
                              <span style={{ color:"#f5a623" }}>E {fmt$(entry)}</span>
                              <span style={{ color:"#00d4a0" }}>TP {fmt$(tpP)}</span>
                            </div>
                            <div style={{ height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, position:"relative", overflow:"visible" }}>
                              <div style={{ height:"100%", width:`${pos}%`, borderRadius:2,
                                background:`linear-gradient(90deg, #ff4466, ${clr})`,
                                transition:"width 0.5s" }}/>
                              {/* Marcador de entrada */}
                              <div style={{ position:"absolute", top:-2, left:`${Math.max(0,Math.min(100,(entry-slP)/range*100))}%`,
                                width:2, height:8, background:"#f5a623", borderRadius:1, transform:"translateX(-50%)" }}/>
                              {/* Marcador precio actual */}
                              <div style={{ position:"absolute", top:-3, left:`${pos}%`,
                                width:3, height:10, background:clr, borderRadius:2, transform:"translateX(-50%)",
                                boxShadow:`0 0 6px ${clr}` }}/>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {/* Grid de datos */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4, marginBottom:6 }}>
                    {[
                      {l:"Entrada", v:ep>0?fmt$(ep):"—",     c:"#8892a4"},
                      {l:"Actual",  v:fmt$(curP),          c: pnl>=0?"#00d4a0":"#ff4466"},
                      {l:"Valor",   v:`$${val.toFixed(2)}`,c:"#e8eaf0"},
                      {l:"Inv.",    v:`$${usdtInv}`,        c:"#4a5568"},
                    ].map(d => (
                      <div key={d.l} style={{ background:"rgba(255,255,255,0.02)", borderRadius:5, padding:"4px 6px" }}>
                        <div style={{ fontSize:7, color:"#4a5568", marginBottom:1 }}>{d.l}</div>
                        <div style={{ fontSize:9, fontFamily:"monospace", color:d.c, fontWeight:600 }}>{d.v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"flex", gap:4, marginBottom:6, flexWrap:"wrap" }}>
                    <span style={{ fontSize:8, padding:"2px 6px", borderRadius:5, background:"rgba(255,68,102,0.1)", color:"#ff4466" }}>SL {fmt$(sl?.sl||sl?.price)}</span>
                    {(sl?.tp||tp?.price) && <span style={{ fontSize:8, padding:"2px 6px", borderRadius:5, background:"rgba(0,212,160,0.1)", color:"#00d4a0" }}>TP {fmt$(sl?.tp||tp?.price)}</span>}
                    {sl?.tpHits > 0 && <span style={{ fontSize:8, padding:"2px 6px", borderRadius:5, background:"rgba(245,166,35,0.1)", color:"#f5a623" }}>TP{sl.tpHits}✓</span>}
                    <span style={{ fontSize:8, padding:"2px 6px", borderRadius:5, background:"rgba(245,166,35,0.1)", color:"#f5a623" }}>Trail↑</span>
                    {sl?.averaged && <span style={{ fontSize:8, padding:"2px 6px", borderRadius:5, background:"rgba(77,166,255,0.1)", color:"#4da6ff" }}>AVG↓</span>}
                    {fundingRates[c.id] && <span style={{ fontSize:8, padding:"2px 6px", borderRadius:5, background:"rgba(255,255,255,0.05)", color:fundingRates[c.id].bullish?"#00d4a0":fundingRates[c.id].bearish?"#ff4466":"#4a5568" }}>FR:{fundingRates[c.id].ratePct}%</span>}
                  </div>
                  {/* Botón venta parcial */}
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 7, color: "#7a8899", letterSpacing: 1, marginBottom: 3 }}>VENTA PARCIAL</div>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[25, 50, 75].map(pct => (
                        <button key={pct} onClick={async () => {
                          const qtyParcial = ((parseFloat(sl?.qty||0) * pct / 100)).toFixed(4);
                          const usdtParcial = (parseFloat(qtyParcial) * curP).toFixed(2);
                          if (!window.confirm(
                            `🟡 VENTA PARCIAL ${pct}% — ${c.symbol}

` +
                            `• Vender: ${qtyParcial} ${c.symbol} (~$${usdtParcial})
` +
                            `• Precio actual: ${fmt$(curP)}
` +
                            `• PnL estimado: ${pnl>=0?"+":""}${pnl.toFixed(2)}%

` +
                            `El SL y TP se recalcularán automáticamente con la qty restante.

¿Confirmas?`
                          )) return;
                          try {
                            addLog(`Ejecutando venta parcial ${pct}% ${c.symbol}...`, "warn");
                            const r = await apiPost("/api/orders/sell-partial", { coinId: c.id, pct });
                            if (r.ok) {
                              if (r.closed) {
                                addLog(`✅ Venta parcial ${c.symbol} (${pct}%) — posición cerrada @ ${fmt$(r.price)} | PnL: ${r.pnlPct}%`, "ok");
                                setStopLosses(prev => { const n={...prev}; delete n[c.id]; return n; });
                                setTakeProfits(prev => { const n={...prev}; delete n[c.id]; return n; });
                              } else {
                                addLog(`✅ Venta parcial ${c.symbol} (${pct}%): vendido ${r.soldQty} @ ${fmt$(r.price)} | Restante: ${r.remaining} | Nuevo SL: ${fmt$(r.newSl)} | TP: ${fmt$(r.newTp)}`, "ok");
                                setStopLosses(prev => ({ ...prev, [c.id]: { ...prev[c.id], qty: r.remaining, sl: r.newSl, tp: r.newTp, tp2: r.newTp2 } }));
                                setTakeProfits(prev => ({ ...prev, [c.id]: { ...prev[c.id], qty: r.remaining, price: r.newTp } }));
                              }
                              await syncFromServer();
                            } else {
                              addLog(`❌ Venta parcial ${c.symbol} fallida: ${r.error}`, "error");
                              alert(`Error: ${r.error}`);
                            }
                          } catch(e) {
                            addLog(`❌ Error venta parcial: ${e.message}`, "error");
                          }
                        }} style={{
                          flex: 1, padding: "5px 2px", borderRadius: 5,
                          border: "1px solid rgba(245,166,35,0.35)",
                          background: "rgba(245,166,35,0.06)",
                          color: "#f5a623", fontFamily: "monospace", fontSize: 8,
                          cursor: "pointer", fontWeight: 700,
                        }}>
                          {pct}%
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Botón venta manual */}
                  <button onClick={async () => {
                    if (!window.confirm(
                      `⚠️ VENTA MANUAL ${c.symbol}

` +
                      `Vas a vender todo el ${c.symbol} al precio de mercado.

` +
                      `• Precio actual: ${fmt$(curP)}
` +
                      `• Qty estimada: ${sl?.qty}
` +
                      `• USDT invertido: $${usdtInv}
` +
                      `• USDT estimado recuperado: $${usdtRec}
` +
                      `• PnL estimado: ${pnl>=0?"+":""}${pnl.toFixed(2)}%
` +
                      `• Comisión: ~$${(parseFloat(sl?.qty||0)*curP*0.002).toFixed(4)} (0.2%)

` +
                      `¿Confirmas la venta?`
                    )) return;
                    try {
                      addLog(`Ejecutando venta manual ${c.symbol}...`, "warn");
                      const r = await apiPost("/api/orders/sell-manual", { coinId: c.id });
                      if (r.ok) {
                        addLog(`✅ Venta manual ${c.symbol}: ${r.qty} @ ${fmt$(r.price)} | PnL: ${r.pnl}% | Recuperado: $${r.usdtRec}`, "ok");
                        // Limpiar posición local y refrescar estado completo
                        setStopLosses(prev => { const n={...prev}; delete n[c.id]; return n; });
                        setTakeProfits(prev => { const n={...prev}; delete n[c.id]; return n; });
                        await syncFromServer(); // refresca trades, posiciones y saldo
                      } else {
                        addLog(`❌ Venta manual ${c.symbol} fallida: ${r.error}`, "error");
                        alert(`Error en venta manual: ${r.error}`);
                      }
                    } catch(e) {
                      addLog(`❌ Error venta manual: ${e.message}`, "error");
                    }
                  }} style={{
                    width: "100%", padding: "6px", borderRadius: 6,
                    border: "1px solid rgba(255,68,102,0.4)",
                    background: "rgba(255,68,102,0.08)",
                    color: "#ff4466", fontFamily: "monospace", fontSize: 9,
                    cursor: "pointer", fontWeight: 700, letterSpacing: 0.5,
                    transition: "all 0.15s"
                  }}
                  onMouseEnter={e => e.target.style.background="rgba(255,68,102,0.18)"}
                  onMouseLeave={e => e.target.style.background="rgba(255,68,102,0.08)"}
                  >
                    🔴 VENDER {c.symbol} AL MERCADO
                  </button>
                  {/* Botón corregir precio de entrada */}
                  <button onClick={async () => {
                    const input = window.prompt(
                      `Corregir precio de entrada de ${c.symbol}\nEntrada actual: ${fmt$(ep)}\n\nIntroduce el precio real de compra:`,
                      ep > 0 ? ep.toFixed(6) : ""
                    );
                    if (!input) return;
                    const newEp = parseFloat(input.replace(",","."));
                    if (isNaN(newEp) || newEp <= 0) { alert("Precio inválido"); return; }
                    const r = await apiPost("/api/orders/fix-entry", { coinId: c.id, entryPrice: newEp }, 30000);
                    if (r.ok) {
                      addLog(`✅ ${c.symbol}: entrada corregida $${r.oldEntry?.toFixed(4)} → $${r.newEntry?.toFixed(4)} | SL:${fmt$(r.sl)} TP:${fmt$(r.tp)}`, "ok");
                      // Actualizar estado local inmediatamente
                      setStopLosses(prev => ({ ...prev, [c.id]: { ...prev[c.id], entryPrice: newEp, sl: r.sl, tp: r.tp, tp2: r.tp2, tp3: r.tp3 } }));
                    } else {
                      alert(`Error: ${r.error}`);
                    }
                  }} style={{
                    width: "100%", marginTop: 4, padding: "5px", borderRadius: 6,
                    border: "1px solid rgba(245,166,35,0.3)",
                    background: "rgba(245,166,35,0.05)",
                    color: "#f5a623", fontFamily: "monospace", fontSize: 8,
                    cursor: "pointer", letterSpacing: 0.5,
                  }}>
                    ✏️ Corregir entrada {fmt$(ep)}
                  </button>
                  {/* Botón añadir posición manual — solo si no hay posición registrada */}
                </div>
              );
            })}
            {/* Botón para añadir posición manual de moneda sin posición registrada */}
            {coinList.filter(c => !stopLosses[c.id]).length > 0 && (
              <div style={{ marginTop: 8, padding: "8px 10px", background: "rgba(77,166,255,0.04)", border: "1px solid rgba(77,166,255,0.12)", borderRadius: 8 }}>
                <div style={{ fontSize: 8, color: "#4da6ff", marginBottom: 6, letterSpacing: 1 }}>➕ AÑADIR POSICIÓN MANUAL</div>
                {coinList.filter(c => !stopLosses[c.id]).map(c => (
                  <button key={c.id} onClick={async () => {
                    const input = window.prompt(`Añadir posición manual de ${c.symbol}\n\nIntroduce el precio de compra real:`);
                    if (!input) return;
                    const ep2 = parseFloat(input.replace(",","."));
                    if (isNaN(ep2) || ep2 <= 0) { alert("Precio inválido"); return; }
                    const r = await apiPost("/api/orders/add-manual", { coinId: c.id, entryPrice: ep2 });
                    if (r.ok) {
                      addLog(`✅ ${c.symbol}: posición añadida @ $${ep2} | SL:${fmt$(r.sl)} TP:${fmt$(r.tp)}`, "ok");
                      setStopLosses(prev => ({ ...prev, [c.id]: { price: r.sl, sl: r.sl, tp: r.tp, qty: r.qty, symbol: c.symbol, pair: c.pair, entryPrice: ep2, tpHits: 0 } }));
                      setTakeProfits(prev => ({ ...prev, [c.id]: { price: r.tp, qty: r.qty, symbol: c.symbol, pair: c.pair, entryPrice: ep2 } }));
                    } else {
                      alert(`Error: ${r.error}`);
                    }
                  }} style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "4px 8px", marginBottom: 3, borderRadius: 5,
                    border: "1px solid rgba(255,255,255,0.06)", background: "transparent",
                    color: "#8892a4", fontFamily: "monospace", fontSize: 8, cursor: "pointer"
                  }}>
                    + {c.symbol}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display:"flex", gap:5, marginTop:4 }}>
              <button onClick={async()=>{
                addLog("Sincronizando posiciones con Bitunix...","info");
                const r = await apiPost("/api/orders/sync");
                if(r.ok) {
                  addLog(`Posiciones sincronizadas: ${r.count} abiertas`,"ok");
                  // Recargar estado completo
                  const state = await apiGet("/api/state");
                  if(state?.openOrders) {
                    const sl = {}, tp = {};
                    Object.entries(state.openOrders).forEach(([id, o]) => {
                      if(o.qty !== "pending") {
                        sl[id] = { price:o.sl, sl:o.sl, tp:o.tp, tp2:o.tp2, tp3:o.tp3, qty:o.qty, symbol:o.symbol, pair:o.pair, entryPrice:o.entryPrice, averaged:o.averaged, tpHits:o.tpHits||0, openTime:o.openTime, adxAtEntry:o.adxAtEntry||0 };
                        tp[id] = { price:o.tp, qty:o.qty, symbol:o.symbol, pair:o.pair, entryPrice:o.entryPrice };
                      }
                    });
                    setStopLosses(sl);
                    setTakeProfits(tp);
                    addLog(`${Object.keys(sl).length} posiciones cargadas en el panel`,"ok");
                  }
                } else {
                  addLog(`Error sincronizando: ${r.error||"desconocido"}`,"error");
                }
              }} style={{ flex:1, padding:"7px", borderRadius:6, border:"1px solid rgba(0,212,160,0.2)", background:"rgba(0,212,160,0.04)", color:"#00d4a0", fontFamily:"monospace", fontSize:8, cursor:"pointer" }}>
                ↕ Bitunix
              </button>
              <button onClick={async()=>{ if(!window.confirm("¿Limpiar posiciones del registro?"))return; await apiPost("/api/orders/clear"); setStopLosses({}); setTakeProfits({}); }} style={{ flex:1, padding:"7px", borderRadius:6, border:"1px solid rgba(255,255,255,0.06)", background:"transparent", color:"#4a5568", fontFamily:"monospace", fontSize:8, cursor:"pointer" }}>
                🗑 limpiar
              </button>
            </div>
            {/* Reset pérdida diaria — elimina ventas erróneas de hoy y reactiva el bot si estaba pausado */}
            <button onClick={async () => {
              if (!window.confirm("¿Eliminar las ventas negativas de HOY del historial y reactivar el bot si estaba pausado por límite diario?")) return;
              const r = await apiPost("/api/trades/reset-daily-loss");
              if (r.ok) {
                const lista = r.tradesEliminados?.map(t => `${t.symbol} (${t.pnl}%)`).join(", ") || "ninguna";
                addLog(`🔄 Reset drawdown diario: ${r.removed} ventas eliminadas [${lista}] | Nuevo drawdown: ${r.nuevasPerdidas} | Bot reactivado: ${r.botReactivado ? "Sí" : "No"}`, "ok");
                if (r.botReactivado) addLog("✅ Bot reactivado automáticamente", "ok");
              } else {
                addLog(`Error en reset drawdown: ${r.error || "desconocido"}`, "error");
              }
            }} style={{
              width: "100%", marginTop: 5, padding: "7px",
              borderRadius: 6, border: "1px solid rgba(231,76,60,0.3)",
              background: "rgba(231,76,60,0.06)", color: "#e74c3c",
              fontFamily: "monospace", fontSize: 8, cursor: "pointer", letterSpacing: 0.5,
            }}>
              🔄 Reset pérdida diaria
            </button>
          </div>

          {/* PORTFOLIO */}
          <div style={{ padding:"12px 14px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <span style={{ fontSize:8, color:"#7a8899", letterSpacing:2 }}>PORTFOLIO</span>
              <span style={{ fontSize:12, fontWeight:700, color:"#e8eaf0", fontFamily:"monospace" }}>${totalBalanceUSD > 0 ? totalBalanceUSD.toFixed(2) : "—"}</span>
            </div>
            {balance?.filter(a => parseFloat(a.balance||0) > 0.0001).length > 0 ? (
              balance.filter(a => parseFloat(a.balance||0) > 0.0001).map((a, i) => {
                const sym    = (a.coin||"").toUpperCase();
                const avail  = parseFloat(a.balance||0);
                const locked = parseFloat(a.balanceLocked||0);
                const c      = COINS.find(x => x.symbol === sym);
                const p      = c ? (prices[c.id]||0) : (sym==="USDT"||sym==="USDC" ? 1 : 0);
                const val    = avail * p;
                const ch     = c ? (changes[c.id]||0) : 0;
                if (val < 0.01) return null;
                return (
                  <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 2px", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color:"#e8eaf0" }}>{sym}</div>
                      <div style={{ fontSize:8, color:"#4a5568", fontFamily:"monospace" }}>{avail.toFixed(sym==="BTC"?6:sym==="ETH"?4:2)}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#00d4a0", fontFamily:"monospace" }}>${val.toFixed(2)}</div>
                      {ch!==0 && <div style={{ fontSize:8, color:ch>=0?"#00d4a0":"#ff4466" }}>{ch>=0?"+":""}{ch.toFixed(2)}%</div>}
                      {locked>0 && <div style={{ fontSize:8, color:"#f5a623" }}>🔒 ${(locked*p).toFixed(2)}</div>}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign:"center", color:"#4a5568", fontSize:9, padding:"10px 0" }}>Cargando saldo...</div>
            )}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5, marginTop:8 }}>
              {[
                { l:"WIN RATE", v:winRateNet!=null?`${winRateNet}%`:"—", c:winRateNet>=50?"#00d4a0":"#ff4466" },
                { l:"TRADES",   v:trades.length, c:"#8892a4" },
              ].map(s => (
                <div key={s.l} style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.04)", borderRadius:6, padding:"6px", textAlign:"center" }}>
                  <div style={{ fontSize:7, color:"#4a5568", marginBottom:2 }}>{s.l}</div>
                  <div style={{ fontSize:12, fontWeight:700, color:s.c, fontFamily:"monospace" }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}


export default function BitunixBot() {
  const [token,  setToken]  = useState(() => { try { return (()=>{try{return localStorage.getItem(TOKEN_KEY)}catch(_){return null}})() || ""; } catch(_) { return ""; } });
  const [authOk, setAuthOk] = useState(false);

  useEffect(() => {
    if (!token) return;
    // Usar master token directamente sin verificar
    const master = process.env.REACT_APP_MASTER_TOKEN;
    if (master && token === master) { setAuthOk(true); return; }
    fetch(`${CONFIG.PROXY}/api/state`, {
      headers: { "x-bot-token": token }
    }).then(r => {
      if (r.ok) setAuthOk(true);
      else { localStorage.removeItem(TOKEN_KEY); setToken(""); }
    }).catch(() => setAuthOk(false));
  }, [token]);

  if (!token || !authOk) {
    return <LoginScreen onLogin={t => { setToken(t); setAuthOk(true); }} />;
  }

  return <BotDashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthOk(false); }} />;
}
